<script lang="ts">
  // The duel arena, a flat, top-down duel mat (the official-playmat layout) floating inside the
  // LINK VRAINS cyberspace (the shared CyberBackground shows through behind it). The whole field +
  // HUD live inside a fixed-aspect LOGICAL STAGE (1600×900) that is uniformly scaled to fit any
  // viewport (EdoPro / Master Duel pattern), so it always keeps its aspect ratio and never clips,
  // shrinking on narrow screens instead of breaking. Dialogs/prompts ride OUTSIDE the stage at true
  // viewport size so they stay prominent. The store owns all duel logic + socket I/O.
  import { onDestroy, onMount } from "svelte";
  import { fade, fly, scale } from "svelte/transition";
  import { flip } from "svelte/animate";
  import { quintOut } from "svelte/easing";
  import { listen, send } from "../stores/net.svelte.ts";
  import { extraTopCode, type QueriedCard } from "@ygoevo/protocol";
  import { duel, sendChoice, leaveDuel, playsFor, actionKindFor, mrefKey, saveReplay, toggleReplayPause, setReplaySpeed, stepReplay, POS, LOCATION, type CardAction, type CardActionKind } from "../stores/duel.svelte.ts";
  import { settings, cycleDuelSpeed } from "../stores/settings.svelte.ts";
  import { device, isPhone } from "../device.svelte.ts";
  import DuelCinematics from "../components/duel/DuelCinematics.svelte";
  import DuelCard from "../components/duel/DuelCard.svelte";
  import CardInfoRail from "../components/duel/CardInfoRail.svelte";
  import RankBadge from "../components/RankBadge.svelte";
  import PortraitPreview from "../components/PortraitPreview.svelte";
  import NpcBustView from "../components/NpcBustView.svelte";
  import { chibiFrom } from "../../render/ChibiAvatar.ts";
  import { loadWorldData } from "../../data.ts";
  import type { NpcBust } from "@ygoevo/yo2-data";
  import PhaseControl from "../components/duel/PhaseControl.svelte";
  import DuelBoard3D from "../components/duel/DuelBoard3D.svelte";
  import DuelMotionLayer from "../components/duel/DuelMotionLayer.svelte";
  import AttackDeclaration from "../components/duel/AttackDeclaration.svelte";
  import DuelStatLabels from "../components/duel/DuelStatLabels.svelte";
  import SideOverlay from "../components/duel/SideOverlay.svelte";
  import DuelPrompts from "../components/duel/DuelPrompts.svelte";
  import DuelDeclare from "../components/duel/DuelDeclare.svelte";
  import CoinFlip from "../components/duel/CoinFlip.svelte";
  import DuelChat from "../components/duel/DuelChat.svelte";
  import { draggableDialog } from "../actions/draggableDialog.ts";
  import {
    nameOf, toggleAutoPass, toggleAutoZone, toggleResponses, setSkipHeld,
    pickCard, pickUnselect, confirmUnselect, isPicked, pickZone, confirmCardPick, cancelCardPick, pickedWeight, openPile, closePile, confirmSurrender, dismissSurrender, zoneLocked,
    toggleProMode, proMenuActive, proPlaysFor, proChainCandidates, proChainPick, proNotify,
  } from "../stores/duel.svelte.ts";
  import { onEscape } from "../hotkeys.ts";

  // 2D-board pile label → pile kind (the 3D board tags meshes directly; here we map the snippet label).
  const PILE_KIND: Record<string, "deck" | "grave" | "extra" | "removed"> = { GY: "grave", DECK: "deck", EXTRA: "extra", BANISH: "removed" };

  type PhaseTarget = "bp" | "m2" | "ep";
  interface Advance { label: string; target: PhaseTarget; choice: CardAction["choice"]; accent: "gold" | "cyan" | "magenta" }
  import { go, session, afterDuelRoute } from "../stores/session.svelte.ts";
  import { cardImage, cardImageLarge } from "../../data.ts";
  import { canonicalCode } from "../cards.ts";
  import { sleeveById, DEFAULT_SLEEVE } from "../duel/sleeves.ts";
  import { DEFAULT_SLEEVE_ID, type RankSchool } from "@ygoevo/world-protocol";

  // The classic card back art, drawn on the face-down deck/extra stacks.
  const deckBack = sleeveById(DEFAULT_SLEEVE).img;

  // One DOM layer is rendered for every physical card in a pile. Flat view uses a tiny diagonal
  // registration offset; perspective view keeps that imperfection but adds genuine translateZ depth.
  function pileLayerStyle(layer: number, mine: boolean): string {
    const direction = mine ? 1 : -1;
    return `--flat-x:${direction * layer * 0.13}px;--flat-y:${direction * layer * 0.1}px;--depth-x:${direction * layer * 0.045}px;--depth-y:${direction * layer * 0.032}px;--depth-z:${layer * 0.34}px`;
  }
  const extraPileCards = (cards: QueriedCard[]): QueriedCard[] =>
    [...cards].sort((a, b) => Number((a.position & POS.FACEUP) !== 0) - Number((b.position & POS.FACEUP) !== 0));

  // Friendly school name for the "uniform unlocked" line on a rated win.
  const uniformName = (s: RankSchool): string =>
    ({ beginner: "", slifer: "Slifer Red", ra: "Ra Yellow", obelisk: "Obelisk Blue", master: "Master" })[s] ?? s;

  const mySleeve = $derived(session.loginOk?.avatar.sleeveId ?? DEFAULT_SLEEVE_ID);

  // ── duelist identity ── the compact player HUD uses the same portrait selection as the rest of the
  // client. The field itself stays clear: the cards and physical arena, not character cut-outs, own the
  // focal area during a duel.
  const myLook = $derived(chibiFrom(session.loginOk?.avatar));
  const oppLook = $derived(duel.oppAvatar ? chibiFrom(duel.oppAvatar) : null);
  // An NPC opponent is drawn from its pre-baked 3×3 bust, looked up lazily by character id.
  let npcBusts = $state<NpcBust[]>([]);
  $effect(() => {
    if (duel.oppNpc && !npcBusts.length) loadWorldData().then((d) => { npcBusts = d.npcs.busts ?? []; });
  });
  const oppBust = $derived(duel.oppNpc ? (npcBusts.find((bn) => bn.id === duel.oppNpc) ?? null) : null);
  $effect(() => {
    if (import.meta.env.DEV && duel.oppNpc && npcBusts.length && !oppBust)
      console.warn(`[Duel] no bust for NPC id "${duel.oppNpc}", flanking portrait + chip head fall back`, npcBusts.map((b) => b.id));
  });
  // Keep the MMO's two duelists present as peripheral waist-up silhouettes. The arena is deliberately
  // much larger than either portrait, and its material surface sits above them, so they frame the duel
  // rather than compete with zones or rail piles.
  const avSize = 420;
  // Each duelist frames the arena from outside its perimeter. The opponent's offsets are
  // HEAD-ANCHORED: a flat bleed can't keep every face clear (bust widths + head positions vary per
  // character — Kaiba's duel disk widens one side), so place the FACE at a fixed inset from the
  // stage edge instead and let the body crop off-screen (owner: face visibility is the priority).
  const myAvBleed = -72;
  const HEAD_INSET = 88; // face-centre distance from the stage's right edge, logical px
  // PvP square portrait / letter fallback: the face sits ≈ box centre.
  const oppAvBleed = HEAD_INSET - avSize / 2;
  // NPC bust: the rendered box is cropped to the occupied 256px columns (NpcBustView geometry);
  // headAnchorX is the pixel-derived face centre baked into the manifest at decode time.
  const npcBleed = $derived.by(() => {
    if (!oppBust?.cells.length) return oppAvBleed;
    const s = avSize / 768;
    const cols = oppBust.cells.map((c) => c.col);
    const minCol = Math.min(...cols);
    const boxW = (Math.max(...cols) - minCol + 1) * 256 * s;
    const headX = ((oppBust.headAnchorX ?? 384) - minCol * 256) * s;
    return Math.round(HEAD_INSET - (boxW - headX));
  });
  // ── scale-to-fit logical stage (EdoPro / MD pattern): author at BASE_W×BASE_H, scale uniformly. ──
  // ONLY the field + hands live inside this aspect-locked stage. The chrome (card-info rail, LP chips,
  // phase orb) rides OUTSIDE it at true viewport size, so the rail fills the full height and the HUD
  // stays readable, while the board squishes into whatever width the (fixed-width) rail leaves it.
  // A landscape logical stage gives mobile horizontal room to keep traditional zones touchable. The
  // old 1180×900 stage was height-bound on phones, then the perspective pitch shrank it again.
  const BASE_W = 1180;
  const BASE_H = 660;
  let vw = $state(typeof window !== "undefined" ? window.innerWidth : BASE_W);
  let vh = $state(typeof window !== "undefined" ? window.innerHeight : BASE_H);
  // Renderer: the WebGL arena (default) or the flat 2D board (toggle). Persisted per device. Both
  // render the Extra Monster Zones in Free Duel, so the choice is purely the player's preference.
  // Phones default to the flat 2D board (the WebGL arena is a battery/thermal gamble on mobile
  // GPUs); the toggle still works and the explicit choice persists either way.
  // ── the 3D board is now CSS (2026-07-11 owner mandate) ─────────────────────────
  // The WebGPU arena hard-hung the main thread mid-duel on some machines (an infinite loop the
  // timer-based watchdogs can't catch — a frozen main thread freezes them too). The replacement is
  // the SAME reliable DOM board presented on a real CSS perspective plane (this codebase's original
  // 3D presentation, before the WebGPU arena existed): identical markup, identical GSAP motion
  // layer (clones fly in screen space; getBoundingClientRect returns PROJECTED rects, so every zone
  // resolve keeps working), plus true translateZ depth for levitation. Nothing that can hang.
  // WebGPU remains for BACKGROUNDS only (CyberBackground). The parked arena is `?force3d=1`.
  let force3d = $state(typeof location !== "undefined" && new URLSearchParams(location.search).get("force3d") === "1");
  // The 3D/flat choice is the CSS tilt now. Desktop defaults 3D; phones default flat (tight layout).
  const storedTilt = typeof localStorage !== "undefined" ? localStorage.getItem("df.dueltilt") : null;
  let tilt3d = $state(storedTilt === null ? !isPhone() : storedTilt !== "0");
  // Set-once duel controls live behind one compact Options trigger on every viewport.
  let togglesOpen = $state(false);
  // `use3d` = the parked WebGPU arena, reachable only via ?force3d=1 for testing.
  const use3d = $derived(force3d && !settings.performanceMode && !duel.preview);
  // The texture-pack editor authors against the FLAT board (alignment vars assume no projection).
  const tilted = $derived(tilt3d && !use3d && !duel.preview);
  function toggle3d() {
    tilt3d = !tilt3d;
    try { localStorage.setItem("df.dueltilt", tilt3d ? "1" : "0"); } catch { /* private mode */ }
  }
  // The parked WebGPU arena (?force3d=1 testing only) failed or hung during init — drop back to
  // the DOM board so the duel never reads as frozen in a blank canvas.
  function arenaInitFailed() {
    force3d = false;
    proNotify("WebGPU arena unavailable — using the standard board");
  }

  // The card-info rail is a real, fixed-width viewport column (never scaled), it eats this much width off
  // the left; the aspect-locked board lives in (and is centred within) the rest. In 3D the detail panel
  // is a click-triggered OVERLAY, so the board takes the full width (no reserved rail).
  // The DOM board (flat OR tilted — same board, only the perspective differs) never reserves a
  // persistent rail: card detail is the dismissible click-to-inspect panel in BOTH, MD-style. (The
  // old always-on left rail is gone; the parked WebGPU arena path used dismissible already.)
  const INFO_W = $derived(0);
  // The phase control docks into a corner on phones, so it no longer steals a permanent 92px gutter
  // from the board. Every usable landscape pixel goes to the field and the player's hand.
  const availW = $derived(vw - INFO_W);
  // uniform scale that fits the stage in the REMAINING viewport (cap upscale at 1.5 so card JPGs stay crisp).
  const stageScale = $derived(Math.min(1.5, Math.min(availW / BASE_W, vh / BASE_H)));
  const offX = $derived(INFO_W + (availW - BASE_W * stageScale) / 2);
  const offY = $derived((vh - BASE_H * stageScale) / 2);
  // The flanking busts follow the VIEWPORT edge, not the stage edge: on any window wider than the
  // stage's aspect, the horizontal letterbox (offX, converted to logical stage px) would otherwise
  // park them mid-screen with the face buried under the field's rail piles (owner report).
  // CLAMPED: at the reported size (~1400×760) offX/stageScale ≈ 18 logical px — that's all the
  // outward push the face needs to clear the rail column. Follow the letterbox only up to 32
  // (that measured need + headroom); past it the busts stay hugging the board. Unclamped, an
  // ultrawide letterbox (2000×838 → ≈198) flung them to the far monitor edges (owner report #2).
  const EDGE_PUSH_MAX = 32;
  const edgePush = $derived(Math.min(EDGE_PUSH_MAX, Math.max(0, offX / stageScale)));
  // Viewport-responsive HUD scale (a UNITLESS number, drives `calc(rem * var(--ui))` sizing on the
  // overlays). Computed in JS because a pure-CSS `clamp()` can't mix a unitless scale with `vw`.
  // Touch floors at 1.1 (not 0.85): every `calc(rem * var(--ui))` overlay — prompt banners,
  // confirm/cancel, phase menu — grows into a usable touch target on phones.
  // Two ramps: the original one caps at 1.18 (reached at vw 1720); a second ULTRAWIDE ramp keeps
  // growing from there to 1.55 by vw≈2100 — capped at 1.18, the HUD read as "tiny" on a 2000px+
  // monitor (owner report). vw ≤ 1720 evaluates bit-identical to the old single-ramp curve.
  const hudUi = $derived(Math.max(device.touch ? 1.1 : 0.85, Math.min(1.18, 0.85 + ((vw - 960) / 760) * 0.33) + Math.min(0.37, Math.max(0, ((vw - 1720) / 380) * 0.37))));
  let sizer = $state<HTMLDivElement | null>(null);
  $effect(() => {
    if (!sizer) return;
    const ro = new ResizeObserver(([e]) => { const r = e!.contentRect; vw = r.width; vh = r.height; });
    ro.observe(sizer);
    return () => ro.disconnect();
  });

  // Stable hand identities so the rows can layout-animate (animate:flip) when the hand re-centres
  // after a draw or play — cards GLIDE to their new spots instead of snapping. Key = passcode + which
  // copy (duplicates count up). The opponent's masked cards (all code 0) degrade to positional keys.
  function handKeyed(hand: QueriedCard[]): { c: QueriedCard; k: string }[] {
    const seen = new Map<number, number>();
    return hand.map((c) => {
      const n = seen.get(c.code) ?? 0;
      seen.set(c.code, n + 1);
      return { c, k: `${c.code}#${n}` };
    });
  }
  const handHidden = (side: "me" | "opp", i: number): boolean => {
    const hk = `hand:${side}:${i}`;
    return duel.hidden.includes(hk) || duel.handHidden.includes(hk);
  };

  const seat = $derived(duel.seat);
  const b = $derived(duel.board);
  const me = $derived(b ? b.players[seat] : null);
  const opp = $derived(b ? b.players[seat === 0 ? 1 : 0] : null);
  const myTurn = $derived(!!b && b.turnPlayer === seat);
  const prompt = $derived(duel.prompt);
  const turnNo = $derived(b?.turn ?? 0);
  const info = (code: number) => duel.names[canonicalCode(code)] ?? null;

  // ── your foils ── load your collection so YOUR cards on the mat wear their holographic
  // finish (opponent cosmetics aren't sent). Best foil grade owned per passcode.
  let myFoils = $state<Record<number, number>>({});
  listen("Inventory", (m) => {
    const next: Record<number, number> = {};
    for (const it of m.items) if (it.foil) next[it.cardId] = Math.max(next[it.cardId] ?? 0, it.foil);
    myFoils = next;
  });
  onMount(() => { if (!duel.preview) send({ t: "InventoryGet" }); });

  // Hold right-click to skip optional response/activation windows (Power-of-Chaos / EDOPro style): the
  // duel flows past chain windows + effect yes/no while held, release to get them back. Window-level so a
  // release off the board (or a tab blur) still un-sticks it; contextmenu suppressed so right-click is ours.
  onMount(() => {
    const down = (e: PointerEvent) => { if (e.button === 2) { e.preventDefault(); setSkipHeld(true); } };
    const up = (e: PointerEvent) => { if (e.button === 2) setSkipHeld(false); };
    const ctx = (e: Event) => e.preventDefault();
    const release = () => setSkipHeld(false);
    window.addEventListener("pointerdown", down);
    window.addEventListener("pointerup", up);
    window.addEventListener("contextmenu", ctx);
    window.addEventListener("blur", release);
    return () => {
      window.removeEventListener("pointerdown", down);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("contextmenu", ctx);
      window.removeEventListener("blur", release);
      setSkipHeld(false);
    };
  });
  // foil grade to show for a card on the mat: only YOUR own cards (controller === seat).
  const foilFor = (card: QueriedCard | null): number =>
    card && card.code && card.controller === seat ? (myFoils[card.code] ?? 0) : 0;

  // ── card-info rail (left): persists the LAST card pointed at (never clears on mouse-leave). ──
  let hovered = $state<QueriedCard | null>(null);
  const hoverInfo = $derived(hovered && hovered.code ? info(hovered.code) : null);
  // 3D board only: a Master-Duel-style card DETAIL that opens on a single click and dismisses on a click
  // elsewhere (the 2D board keeps the persistent hover rail above). Click-only — never set on hover.
  let selectedCard = $state<QueriedCard | null>(null);
  const selectedInfo = $derived(selectedCard && selectedCard.code ? info(selectedCard.code) : null);
  // dismiss on a click that isn't on the board (handled by the arena), the detail panel, or the action
  // menu (which owns its own backdrop). Only armed while a detail is open with no menu over it.
  $effect(() => {
    if (!selectedCard || menu) return;
    const onDown = (e: PointerEvent) => {
      if ((e.target as Element | null)?.closest?.("canvas, .tp-cardinfo, .tp-chat")) return;
      selectedCard = null;
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  });
  // In the mod-editor preview, seed the inspector with a card on the board so the Card Info rail shows
  // real content (a card + chat) instead of the empty "hover a card" hint.
  $effect(() => {
    if (duel.preview && !hovered) hovered = me?.mzone.find(Boolean) ?? me?.hand[0] ?? null;
  });
  // The card the pointer is on RIGHT NOW (clears on leave), drives the equip/target relationship glow.
  let linkAnchor = $state<QueriedCard | null>(null);
  function setHover(card: QueriedCard, on: boolean) {
    if (on && card.code) hovered = card;   // info rail persists
    linkAnchor = on ? card : null;          // relationship highlight is live (clears on leave)
  }

  // ── equip / target relationship highlight ──────────────────────────────────────
  // Hovering a card lights a chain-link badge on it AND every card it's linked to: an equip card ⇄ the
  // monster it's equipped to, and any continuous/effect targeter ⇄ its target(s). The core reports each
  // card's OWN outgoing links (equip host + target list), so we scan the board to make it two-way and
  // transitive (a monster with several equips, a card targeting several, all light up together).
  const refKey = (r: { controller: number; location: number; sequence: number }) => `${r.controller}|${r.location}|${r.sequence}`;
  const linkGroup = $derived.by((): Set<string> => {
    const a = linkAnchor;
    if (!a || !b) return new Set();
    const anchor = refKey(a);
    const group = new Set<string>([anchor]);
    for (const pl of b.players) {
      for (const c of [...pl.mzone, ...pl.szone]) {
        if (!c) continue;
        const ck = refKey(c);
        if (c.equip) { const ek = refKey(c.equip); if (ck === anchor) group.add(ek); if (ek === anchor) group.add(ck); }
        if (c.targets) for (const t of c.targets) { const tk = refKey(t); if (ck === anchor) group.add(tk); if (tk === anchor) group.add(ck); }
      }
    }
    return group.size > 1 ? group : new Set(); // only show when there's an actual relationship
  });
  const isLinked = (card: QueriedCard): boolean => linkGroup.has(refKey(card));

  // attack flash drives a brief board shake.
  const attacking = $derived(duel.fx.some((f) => f.kind === "attack"));

  // "Opponent is thinking" pill, only once we've CAUGHT UP on playback (not mid-animation) and it's
  // genuinely the opponent's decision (no prompt of ours pending), so it reads as "waiting on them".
  const oppThinking = $derived(
    duel.oppThinking && !duel.streaming && !prompt && !duel.cardPick && !duel.unselectPick && !duel.placePick && !duel.ended,
  );

  // ── per-card action menu (always opens on click, so the player sees WHAT a click does) ──
  let menu = $state<{ x: number; y: number; actions: CardAction[]; card: QueriedCard } | null>(null);
  // Effect picker: a card with 2+ activatable effects shows ONE "Activate"; clicking it opens this so
  // the player chooses WHICH effect (same dialog shell as the position / yes-no modal).
  let effectPick = $state<{ title: string; code: number; options: CardAction[] } | null>(null);
  // The card's current play category (null = no play), one source of truth for the 2D border AND the
  // 3D frame, so both boards colour-code plays identically (cyan=play, magenta=effect/special-summon,
  // gold=attack, lime=pick target).
  const playKind = (card: QueriedCard | null): CardActionKind | null => (card ? actionKindFor(card) : null);
  // Per-card interaction state for the 3D board (coloured glow border + attack sword). Pro Mode's
  // neutral "pro" kind must draw NO glow/sword in the arena either — clicks still land (the arena
  // raycasts every card mesh regardless of state), only the affordance is withheld.
  const cardStateFor = (c: QueriedCard) => {
    const kind = actionKindFor(c);
    return { selectable: kind != null && kind !== "pro", attackable: kind === "attack", special: kind === "special", target: kind === "target", targeted: duel.targeted.includes(mrefKey(c.controller, c.location, c.sequence)) };
  };
  // Manual zone-pick (auto-zone OFF): is this one of your own zones the player may place into now? The
  // 3D arena lights these via the WebGL layer; the 2D board lights them via this check (the bug was 2D
  // had no manual-placement handling at all, only 3D did).
  const isPlaceTarget = (location: number, sequence: number): boolean =>
    !!duel.placePick && duel.placePick.targets.some((t) => t.controller === seat && t.location === location && t.sequence === sequence);
  // Chain-link badge lookup (2D board): the link number activated from this zone, or null. Field zones
  // match controller+location+seq; piles (one stack) match controller+location, any seq.
  const PILE_LOCS = LOCATION.GRAVE | LOCATION.DECK | LOCATION.EXTRA | LOCATION.REMOVED;
  function chainAt(location: number, controller: number, seq: number): number | null {
    const pile = (location & PILE_LOCS) !== 0;
    for (const c of duel.chainLinks) {
      if (c.controller !== controller || (c.location & location) === 0) continue;
      if (pile || c.sequence === seq) return c.link;
    }
    return null;
  }
  function clickCard(card: QueriedCard, e?: MouseEvent) {
    // An on-board pick is active → clicking a candidate SELECTS it (no action menu, nothing sent —
    // Confirm does that) and inspects it, so you can check which face-down card you're targeting.
    if (duel.cardPick) {
      if (card.code) { hovered = card; selectedCard = card; }
      pickCard(card);
      menu = null;
      return;
    }
    // An incremental cost pick (tribute/discard) → clicking a candidate toggles that one card.
    if (duel.unselectPick) { pickUnselect(card); menu = null; return; }
    // Clicking any visible card opens its detail: the persistent inspector rail (2D) AND the
    // click-triggered Master-Duel detail panel (3D). Works on an opponent's face-up card too (no play).
    if (card.code) { hovered = card; selectedCard = card; }
    // Pro Mode: the full type-based menu (legal AND illegal entries, indistinguishable) instead of
    // the prompt-filtered one. Same menu shell, so a legal pick fires the exact same DuelChoice.
    const acts = proMenuActive() ? proPlaysFor(card) : playsFor(card);
    if (acts.length === 0) return;
    // ALWAYS open the menu (even for a single action) so the click is never a mystery.
    const x = e?.clientX ?? window.innerWidth / 2;
    const y = e?.clientY ?? window.innerHeight / 2;
    const h = 44 + acts.length * 50;
    menu = { x: Math.min(x, window.innerWidth - 224), y: Math.min(Math.max(8, y - 20), window.innerHeight - h - 8), actions: acts, card };
  }

  // Chromium occasionally resolves a CSS-3D hit to the tilted plane instead of the transformed card
  // painted above it. Keep native card events as the primary path, then geometrically recover only
  // when the plane itself won the hit test. The closest projected centre disambiguates overlapping
  // bounding boxes near the camera edge.
  function projectedNodeAtPoint(root: HTMLElement, selector: string, x: number, y: number): HTMLElement | null {
    let best: HTMLElement | null = null;
    let bestScore = Number.POSITIVE_INFINITY;
    for (const node of root.querySelectorAll<HTMLElement>(selector)) {
      const r = node.getBoundingClientRect();
      if (x < r.left || x > r.right || y < r.top || y > r.bottom || r.width <= 0 || r.height <= 0) continue;
      const dx = (x - (r.left + r.width / 2)) / r.width;
      const dy = (y - (r.top + r.height / 2)) / r.height;
      const score = dx * dx + dy * dy;
      if (score < bestScore) { best = node; bestScore = score; }
    }
    return best;
  }
  function fieldCardByRef(key: string | undefined): QueriedCard | null {
    if (!key || !b) return null;
    for (const p of b.players) {
      for (const c of [...p.mzone, ...p.szone]) if (c && mrefKey(c.controller, c.location, c.sequence) === key) return c;
    }
    return null;
  }
  let projectedCardHover: QueriedCard | null = null;
  let projectedZoneHover: HTMLElement | null = null;
  function clearProjectedHover(): void {
    if (projectedCardHover) setHover(projectedCardHover, false);
    projectedCardHover = null;
    projectedZoneHover?.classList.remove("zone-slot-hovered");
    projectedZoneHover = null;
  }
  function trackProjectedHover(e: PointerEvent): void {
    const root = e.currentTarget as HTMLElement;
    const origin = e.target instanceof Element ? e.target : null;
    // Native card hover is more precise when Chromium does provide it.
    const nativeCardNode = origin?.closest<HTMLElement>(".field-card-anchor[data-card-ref]");
    const nativeCard = fieldCardByRef(nativeCardNode?.dataset.cardRef);
    if (origin?.closest(".tp-card") && nativeCard) {
      projectedZoneHover?.classList.remove("zone-slot-hovered");
      projectedZoneHover = null;
      if (projectedCardHover && projectedCardHover !== nativeCard) setHover(projectedCardHover, false);
      projectedCardHover = nativeCard;
      setHover(nativeCard, true);
      return;
    }
    if (origin?.closest("button")) { clearProjectedHover(); return; }
    const cardNode = projectedNodeAtPoint(root, ".field-card-anchor[data-card-ref]", e.clientX, e.clientY);
    const card = fieldCardByRef(cardNode?.dataset.cardRef);
    if (card) {
      projectedZoneHover?.classList.remove("zone-slot-hovered");
      projectedZoneHover = null;
      if (projectedCardHover !== card) {
        if (projectedCardHover) setHover(projectedCardHover, false);
        projectedCardHover = card;
        setHover(card, true);
      }
      return;
    }
    if (projectedCardHover) setHover(projectedCardHover, false);
    projectedCardHover = null;
    const zone = projectedNodeAtPoint(root, ".field-slot-empty", e.clientX, e.clientY);
    if (zone === projectedZoneHover) return;
    projectedZoneHover?.classList.remove("zone-slot-hovered");
    projectedZoneHover = zone;
    projectedZoneHover?.classList.add("zone-slot-hovered");
  }
  function clickProjectedField(e: MouseEvent): void {
    const origin = e.target instanceof Element ? e.target : null;
    if (origin?.closest(".tp-card, button, [role='button']")) return;
    const root = e.currentTarget as HTMLElement;
    const cardNode = projectedNodeAtPoint(root, ".field-card-anchor[data-card-ref]", e.clientX, e.clientY);
    const card = fieldCardByRef(cardNode?.dataset.cardRef);
    if (card) clickCard(card, e);
  }
  function projectedFieldInteraction(node: HTMLElement) {
    node.addEventListener("click", clickProjectedField);
    node.addEventListener("pointermove", trackProjectedHover);
    node.addEventListener("pointerleave", clearProjectedHover);
    return {
      destroy() {
        node.removeEventListener("click", clickProjectedField);
        node.removeEventListener("pointermove", trackProjectedHover);
        node.removeEventListener("pointerleave", clearProjectedHover);
      },
    };
  }
  function runAction(a: CardAction) {
    // Pro Mode illegal pick: send NOTHING, surface the notification, and KEEP the menu open so
    // trying the next action is one click away (the learning loop must feel cheap).
    if (a.illegal) { proNotify(a.illegal); return; }
    // Multi-effect Activate → ask which effect instead of firing blindly.
    if (a.effects && a.effects.length > 1) {
      const c = menu?.card;
      effectPick = {
        title: "Choose an effect to activate",
        code: c?.code ?? 0,
        options: a.effects.map((e) => ({ label: e.label, choice: e.choice, accent: "magenta" })),
      };
      menu = null;
      return;
    }
    sendChoice(a.choice);
    menu = null;
    selectedCard = null; // an action fired → close the card detail (the card is moving)
    if (duel.pileView) closePile(); // a GY/Extra action fired from the pile viewer → its snapshot is stale
  }

  // Every legal phase-advance, surfaced explicitly so the player can pick.
  const advances = $derived.by((): Advance[] => {
    if (!myTurn) return [];
    const p = prompt;
    const out: Advance[] = [];
    if (p?.kind === "selectIdleCmd") {
      if (p.canBattlePhase) out.push({ label: "To Battle", target: "bp", choice: { kind: "idle", cmd: 6 }, accent: "gold" });
      if (p.canEndPhase) out.push({ label: "End Turn", target: "ep", choice: { kind: "idle", cmd: 7 }, accent: "magenta" });
    } else if (p?.kind === "selectBattleCmd") {
      if (p.canMain2) out.push({ label: "Main 2", target: "m2", choice: { kind: "battle", cmd: 2 }, accent: "cyan" });
      if (p.canEndPhase) out.push({ label: "End Turn", target: "ep", choice: { kind: "battle", cmd: 3 }, accent: "magenta" });
    }
    return out;
  });

  // The "harder" declare/order prompts get their own bottom panel (DuelDeclare).
  const DECLARE_KINDS = ["selectSum", "selectCounter", "announceRace", "announceAttrib", "announceCard", "announceNumber", "sortCard", "sortChain"];
  const declarePrompt = $derived(!!prompt && DECLARE_KINDS.includes(prompt.kind));

  // ── EDOPro-style player clock ──
  // The server owns each player's cumulative bank and chooses whose clock is active at every core
  // prompt. Locally derive from one monotonic deadline so background-tab throttling cannot add time.
  let timerNow = $state(typeof performance !== "undefined" ? performance.now() : 0);
  $effect(() => {
    if (duel.ended || duel.replayMode) return;
    const iv = setInterval(() => { timerNow = performance.now(); }, 250);
    return () => clearInterval(iv);
  });
  const timerPlayer = $derived(duel.timerActivePlayer ?? duel.timerLastPlayer ?? b?.turnPlayer ?? 0);
  const timerOwnerMine = $derived(timerPlayer === seat);
  const timerRemainingMs = $derived.by(() => {
    const base = duel.timerRemainingMs[timerPlayer] ?? 0;
    const elapsed = duel.timerActivePlayer === timerPlayer ? Math.max(0, timerNow - duel.timerSyncedAt) : 0;
    return Math.max(0, base - elapsed);
  });
  const timerSecs = $derived(Math.ceil(timerRemainingMs / 1000));
  const timerPct = $derived(duel.timerLimitMs > 0 ? (timerRemainingMs / duel.timerLimitMs) * 100 : 0);

  // Modal decisions (no card target): position / yes-no.
  const modal = $derived.by(() => {
    const p = prompt;
    if (p?.kind === "selectPosition") {
      const opts: CardAction[] = [];
      // Each button must answer with a bit that's IN the allowed mask — a hardcoded face-up bit gets
      // rejected by the core when only the face-down bit is legal (Cyber Jar's attack-or-set), which
      // re-issues the same prompt forever (popup "closes and reopens" until timeout).
      if (p.positions & (POS.FACEUP_ATTACK | POS.FACEDOWN_ATTACK)) {
        const pos = p.positions & POS.FACEUP_ATTACK ? POS.FACEUP_ATTACK : POS.FACEDOWN_ATTACK;
        opts.push({ label: "Attack Position", choice: { kind: "position", pos }, accent: "gold" });
      }
      if (p.positions & (POS.FACEUP_DEFENSE | POS.FACEDOWN_DEFENSE)) {
        const pos = p.positions & POS.FACEUP_DEFENSE ? POS.FACEUP_DEFENSE : POS.FACEDOWN_DEFENSE;
        opts.push({ label: p.positions & POS.FACEUP_DEFENSE ? "Defense Position" : "Set", choice: { kind: "position", pos }, accent: "cyan" });
      }
      return { title: "Choose a battle position", code: p.code, options: opts };
    }
    if (p?.kind === "selectYesNo" || p?.kind === "selectEffectYN") {
      return {
        title: p.description || "Activate this effect?",
        code: p.kind === "selectEffectYN" ? p.card.code : 0,
        options: [
          { label: "Yes", choice: { kind: "yesno", yes: true }, accent: "lime" } as CardAction,
          { label: "No", choice: { kind: "yesno", yes: false }, accent: "magenta" } as CardAction,
        ],
      };
    }
    return null;
  });

  // Where the current on-board pick's candidates live, for accurate wording ("in your hand" vs
  // "on the board") and to drive which cards glow.
  const pickLoc = $derived.by((): string => {
    if (!duel.cardPick) return "on the board";
    const t = duel.cardPick.targets;
    if (t.every((c) => (c.location & LOCATION.HAND) !== 0)) return "in your hand";
    if (t.every((c) => (c.location & (LOCATION.GRAVE | LOCATION.REMOVED | LOCATION.DECK | LOCATION.EXTRA)) !== 0)) return "in the list";
    return "on the board";
  });

  const ACCENT: Record<string, string> = {
    cyan: "border-cyan/60 text-cyan hover:bg-cyan/15",
    magenta: "border-magenta/60 text-magenta hover:bg-magenta/15",
    gold: "border-gold/55 text-gold hover:bg-gold/15",
    lime: "border-lime/60 text-lime hover:bg-lime/15",
  };
  const lp = (n: number) => Math.max(0, Math.min(100, (n / 8000) * 100));
  // LP stays within the VRAINS palette: each duelist keeps their cyan/magenta energy line, while a
  // low total earns the shared danger-magenta treatment rather than a disconnected traffic-light bar.
  const lpFill = (n: number, col: string) =>
    n <= 2000
      ? "linear-gradient(90deg,#ff5b78,#ff2d7e)"
      : `linear-gradient(90deg,color-mix(in oklab,${col} 68%,white),${col})`;

  // Escape backs out one layer at a time: open menu → pile view → surrender prompt
  // → a cancelable card pick. Migrated to TanStack Hotkeys (see lib/hotkeys.ts).
  onEscape(() => {
    if (togglesOpen) { togglesOpen = false; return; }
    if (menu) { menu = null; return; }
    if (selectedCard) { selectedCard = null; return; }
    if (duel.pileView) { closePile(); return; }
    if (duel.askSurrender) { dismissSurrender(); return; }
    if (duel.cardPick?.cancelable) { cancelCardPick(); return; }
    if (duel.unselectPick?.cancelable) { confirmUnselect(); return; } // -1 cancels an optional cost pick
    menu = null;
  });

  onDestroy(() => leaveDuel());
</script>

<!-- One interactive card slot (click/hover wired to the board). `flip` rotates the card 180° so the
     opponent's field reads from THEIR side of the mat (real cross-table orientation). -->
{#snippet cell(card: QueriedCard | null, label: string, flip = false)}
  <DuelCard
    {card}
    info={card?.code ? info(card.code) : null}
    foil={foilFor(card)}
    {label}
    action={playKind(card)}
    flip180={flip}
    active={!!card && menu?.card === card}
    picked={!!card && isPicked(card)}
    linked={!!card && isLinked(card)}
    onclick={card ? (e?: MouseEvent) => clickCard(card, e) : undefined}
    onhover={card ? (on) => setHover(card, on) : undefined}
  />
{/snippet}

<!-- A face-down pile (deck / extra) or face-up top-card pile (graveyard / banished). GY / banished
     open a viewer (generous hit area + gold hover ring); your own deck raises the surrender confirm. -->
{#snippet pile(count: number, label: string, topCode: number, col: string, mine: boolean, size = 1, cards: QueriedCard[] = [])}
  {@const kind = PILE_KIND[label]}
  {@const viewable = kind === "grave" || kind === "removed"}
  {@const pileMark = kind === "grave" ? "GY" : kind === "removed" ? "B" : kind === "extra" ? "ED" : "D"}
  {@const ploc = kind === "grave" ? LOCATION.GRAVE : kind === "deck" ? LOCATION.DECK : kind === "extra" ? LOCATION.EXTRA : kind === "removed" ? LOCATION.REMOVED : 0}
  {@const cnum = ploc ? chainAt(ploc, mine ? seat : 1 - seat, -1) : null}
  {@const topLayer = Math.max(0, count - 1)}
  <button
    type="button"
    class="pile-zone group relative grid place-items-center transition-transform hover:scale-[1.045]"
    style="--pile-default:{size};width:calc(var(--zone-side) * var(--psize, var(--pile-default)));height:calc(var(--zone-track) * var(--psize, var(--pile-default)))"
    title={kind === "deck" && mine ? "Your deck, click to surrender" : viewable ? (mine ? `View your ${label}` : `View ${col === 'var(--color-magenta)' ? 'opponent' : 'your'} ${label}`) : label}
    aria-label={kind === "deck" && mine ? `Your deck, ${count} cards. Click to surrender.` : `${label}, ${count} cards`}
    onclick={() => kind && openPile(kind, mine)}
  >
    <!-- Piles use the same transparent printed frame as field zones; the live CyberBackground remains
         visible through every empty socket. -->
    <div class="pile-socket absolute inset-0 chamfer {mine ? 'text-cyan/55' : 'text-magenta/55'}" class:socket-opp={!mine} style="--c:12px"></div>
    <div class="pile-stack relative aspect-[59/86]" class:pile-opp={!mine} style="--stack-count:{count};color:{col};width:calc(var(--cw) * 1.02 * var(--psize, var(--pile-default)))">
      <!-- The motion target follows the physical top of the stack. This keeps summon/send animations
           registered to the visible card instead of the empty socket beneath the pile. -->
      <div class="pile-motion-anchor pointer-events-none absolute inset-0" data-mref={ploc ? mrefKey(mine ? seat : 1 - seat, ploc, 0) : undefined} style={pileLayerStyle(topLayer, mine)}></div>
      {#if count > 0}
        <div class="pile-ground-shadow pointer-events-none absolute inset-0" style={pileLayerStyle(topLayer, mine)}></div>
        <!-- A real layer for every real card. Known public piles use their actual art; hidden piles use
             the shared sleeve. The tiny registration drift remains visible in top-down mode, while
             perspective adds genuine Z separation between the same physical layers. -->
        {#each Array.from({ length: count }) as _, layer (layer)}
          {@const layerCard = cards[layer]}
          {@const layerCode = layerCard ? ((layerCard.position & POS.FACEUP) !== 0 ? layerCard.code : 0) : topCode}
          <div
            class="pile-card-layer pointer-events-none absolute inset-0 rounded-[5px]"
            class:pile-card-layer-top={layer === count - 1}
            style={pileLayerStyle(layer, mine)}
            aria-hidden="true"
          >
            <div class="pile-card-art size-full overflow-hidden rounded-[5px]" class:rotate-180={!mine}>
              {#if layerCode}
                <img src={cardImage(layerCode)} alt="" class="size-full object-cover" draggable="false" />
              {:else if deckBack}
                <img src={deckBack} alt="" class="size-full object-cover" draggable="false" />
              {:else}
                <div class="grid size-full place-items-center bg-gradient-to-br from-surface-2 to-void-2">
                  <div class="size-5 rotate-45" style="border:1px solid color-mix(in oklab,{col} 50%,transparent);background:color-mix(in oklab,{col} 10%,transparent)"></div>
                </div>
              {/if}
            </div>
          </div>
        {/each}
        <span class="pointer-events-none pile-count pile-follows-top absolute bottom-1 right-1 z-[80] grid size-5 place-items-center rounded-full bg-void/95 font-display text-[0.64rem] font-extrabold tabular-nums text-white" style={`${pileLayerStyle(topLayer, mine)};-webkit-text-stroke:.8px #05070d;paint-order:stroke fill;text-shadow:0 1px 2px rgba(0,0,0,.9);box-shadow:0 0 0 1px color-mix(in oklab,${col} 52%,transparent),0 2px 5px rgba(0,0,0,.55)`}>{count}</span>
        {#if viewable}
          <div class="pointer-events-none pile-view-ring pile-follows-top absolute inset-0 z-[70] rounded-[5px] opacity-0 ring-1 ring-inset ring-gold/70 transition-opacity group-hover:opacity-100" style={pileLayerStyle(topLayer, mine)}></div>
        {/if}
      {/if}
      <!-- empty pile: nothing here — the square socket behind IS the placeholder -->
    </div>
    <span class="pile-caption pointer-events-none absolute left-1/2 top-full z-20 -translate-x-1/2 font-display text-[0.48rem] font-bold uppercase tracking-[0.14em] {mine ? 'text-cyan/75' : 'text-magenta/75'}">{pileMark}</span>
    {#if cnum}
      <!-- the effect fired FROM this pile: the activating card rises off the stack and hovers with the
           chain badge on it (the 2D levitation), so "my GY effect is resolving" reads on the board. -->
      {@const link = duel.chainLinks.find((c) => c.link === cnum)}
      {#if link?.code}
        <div class="chain-hover-card pointer-events-none absolute left-1/2 z-20 w-[86%] -translate-x-1/2 chamfer overflow-hidden" class:rotate-180={!mine} style="--c:4px;top:-14%" in:fly={{ y: 8, duration: 220, easing: quintOut }} out:fade={{ duration: 160 }}>
          <img src={cardImage(link.code)} alt={nameOf(link.code)} class="block aspect-[59/86] w-full object-cover" draggable="false" />
        </div>
      {/if}
      {@render chainBadge(cnum)}
    {/if}
  </button>
{/snippet}

<!-- Locked-zone overlay (Ground Collapse, etc.): a magenta hazard-hatched fill + padlock on a zone no
     card can occupy. Sits over the empty slot so the dead zone reads at a glance. Decorative only. -->
{#snippet lockBadge()}
  <div class="pointer-events-none absolute inset-0 z-10 grid place-items-center chamfer overflow-hidden"
       style="--c:4px" title="Zone locked">
    <div class="absolute inset-0" style="background:
        repeating-linear-gradient(45deg, color-mix(in oklab,var(--color-magenta) 22%,transparent) 0 5px, transparent 5px 11px),
        color-mix(in oklab,var(--color-magenta) 10%, var(--color-void) 78%);
      box-shadow:inset 0 0 0 1px color-mix(in oklab,var(--color-magenta) 60%,transparent)"></div>
    <span class="relative text-[1.1rem] leading-none"
          style="color:color-mix(in oklab,var(--color-magenta) 85%, white);filter:drop-shadow(0 0 6px color-mix(in oklab,var(--color-magenta) 65%,transparent))">🔒</span>
  </div>
{/snippet}

<!-- Chain marker for the 2D board: the CLASSIC treatment — a golden ring of chain links slowly
     rotating OVER the activating card with the link number on its centre. Pops in on activation,
     scales out when that link resolves (the {#if} drives it). -->
{#snippet chainBadge(n: number)}
  <!-- chain-overlay: real translateZ depth — the board's preserve-3d paints by Z, so z-index alone
       loses to the card volume's 7px lift / a pile's stacked layers (the "card hides the chain" bug) -->
  <div class="chain-overlay pointer-events-none absolute inset-0 z-30 grid place-items-center" transition:scale={{ duration: 200, start: 0.4 }}>
    <!-- dark scrim dims the card art so the gold ring + number read at full contrast on ANY card -->
    <div class="absolute inset-0 rounded-[3px] bg-[#02040a]/55"></div>
    <svg viewBox="0 0 100 100" class="chain-ring absolute" style="width:118%;max-width:none" aria-hidden="true">
      <g fill="none" stroke-linejoin="round">
        {#each Array.from({ length: 12 }) as _, i (i)}
          {@const flat = i % 2 === 0}
          <rect x={flat ? 44.5 : 45.5} y={flat ? 5.4 : 7.6} width={flat ? 11 : 9} height={flat ? 6.4 : 2.6}
                rx={flat ? 3.2 : 1.3} stroke={flat ? "#5a3c06" : "#4a3105"} stroke-width={flat ? 3.4 : 2.6}
                transform="rotate({i * 30} 50 50)" />
          <rect x={flat ? 44.5 : 45.5} y={flat ? 5.4 : 7.6} width={flat ? 11 : 9} height={flat ? 6.4 : 2.6}
                rx={flat ? 3.2 : 1.3} stroke={flat ? "#ffd76a" : "#e8b93f"} stroke-width={flat ? 2 : 1.4}
                transform="rotate({i * 30} 50 50)" />
        {/each}
      </g>
    </svg>
    <span class="chain-num relative font-display font-extrabold tabular-nums">{n}</span>
  </div>
{/snippet}

<!-- BECOME_TARGET reticle: a magenta crosshair ring over a card an effect just picked, so the player
     sees WHICH card was targeted (e.g. which set Dark World Lightning hit) before it's destroyed. -->
{#snippet targetMark()}
  <!-- same preserve-3d depth fix as the chain badge: the reticle must beat the card's Z lift -->
  <div class="chain-overlay pointer-events-none absolute inset-0 z-30 grid place-items-center" transition:scale={{ duration: 180, start: 0.5 }}>
    <div class="target-mark absolute inset-0 chamfer" style="--c:4px"></div>
    <span class="reticle" style="width:46%;aspect-ratio:1;color:var(--color-magenta)"></span>
  </div>
{/snippet}

<!-- One zone cell, Master-Duel style: a SQUARE socket plate (side = card height, chamfered octagon,
     tinted per side) that is ALWAYS visible — the narrower portrait card sits ON it, peeking the
     socket around its edges like MD's stone tiles. The square also gives a defense-position monster
     (rotated 90°) room to turn without overlapping neighbours. -->
{#snippet zoneSquare(card: QueriedCard | null, flip: boolean, mine: boolean, loc: number, seq: number, label = "", neutral = false)}
  {@const ctrl = mine ? seat : 1 - seat}
  {@const cnum = chainAt(loc, ctrl, seq)}
  {@const mk = mrefKey(ctrl, loc, seq)}
  <!-- CELL footprint is card-width (a hair over), so the 5 zones sit CLOSE like MD — the card-height
       square used to space the narrow cards far apart. Height stays a full card so the two rows never
       overlap; a defense monster (rotated wide) simply overhangs into the gutter, z-raised. -->
  <div class="field-slot relative grid shrink-0 place-items-center" class:field-slot-empty={!card} style="height:calc(var(--zone-row-h, var(--zone-track)) * var(--zsize, 1));width:calc(var(--zone-side) * var(--zsize, 1))">
    <!-- the SQUARE socket plate (side ≈ card width), centred — the card stands a touch taller than
         its tile, MD-style. Also the texture-pack .tp-zone hook (--df-zone-dashed contract). -->
    <!-- `inset:0` is deliberate: transform-centering a socket inside a perspective ancestor applies
         its -50% offset in a different coordinate space, visually separating the card from its zone. -->
    <div class="tp-zone zone-socket absolute inset-0 chamfer grid place-items-center {neutral ? 'zone-socket-neutral' : mine ? 'text-cyan/55' : 'text-magenta/55'}" class:socket-opp={!mine && !neutral} style="--c:12px">
      {#if !card && label}<span class="font-display text-[0.44rem] font-bold uppercase tracking-[0.16em]">{label}</span>{/if}
    </div>
    {#if !card}
      <!-- Only this explicit surface participates in empty-zone hover hit testing. The structural
           field geometry itself stays pointer-transparent, preventing projected empty rows from
           stealing clicks from visible cards in perspective mode. -->
      <div class="zone-hover-target absolute inset-0 z-0" aria-hidden="true"></div>
    {/if}
    <!-- data-mref lets the motion layer find this zone's landing rect; visibility hides the card
         while it's flying out so there's no ghost under the flyer. --df-card-x/y nudge the CARD
         inside the socket (playmat alignment). Kept card-sized even when empty: inbound flights
         resolve their destination rect BEFORE the card exists. -->
    <div class="field-card-anchor relative" class:chain-levit={!!cnum} data-mref={mk} data-card-ref={card ? mk : undefined} style="width:calc(var(--cw) * var(--zsize, 1) * 1.02);transform:translate(var(--df-card-x, 0px), var(--df-card-y, 0px));visibility:{duel.hidden.includes(mk) || duel.handHidden.includes(mk) ? 'hidden' : 'visible'}">
      {#if card}
        <div class="field-card-volume relative aspect-[59/86] w-full">
          <div class="field-card-shadow pointer-events-none absolute inset-0"></div>
          <div class="field-card-face relative size-full">{@render cell(card, "", flip)}</div>
        </div>
      {:else}
        <div class="aspect-[59/86] w-full"></div>
      {/if}
      {#if cnum}{@render chainBadge(cnum)}{/if}
      {#if duel.targeted.includes(mk)}{@render targetMark()}{/if}
    </div>
    {#if mine && isPlaceTarget(loc, seq)}
      <!-- Manual zone-pick highlight (auto-zone off): the full plate is the target, not just the
           portrait-card footprint. That keeps a large Master-Duel-style socket equally easy to tap. -->
      <button class="place-target absolute inset-0 z-20 chamfer" style="--c:10px" aria-label="Place a card in this zone" onclick={() => pickZone(seat, loc, seq)}></button>
    {/if}
    {#if zoneLocked(ctrl, loc, seq)}{@render lockBadge()}{/if}
  </div>
{/snippet}

<!-- The 2 Extra Monster Zones — there are only TWO, and they are SHARED by both players (Master Rules),
     so they render ONCE on the centre line (aligned to the 2nd & 4th columns), not per-player. The two
     physical zones map across the table: from your seat, the left zone is your seq 5 = opponent's seq 6,
     the right is your seq 6 = opponent's seq 5. Each zone shows whichever player occupies it (a card you
     don't control is flipped to read from their side); an empty zone is YOUR placement target (seq 5/6).
     Free Duel only; World duels never render these. -->
{#snippet emzRow()}
  {@const M = me!}
  {@const O = opp!}
  {@const L = M.mzone[5] ? { c: M.mzone[5], mine: true, seq: 5 } : O.mzone[6] ? { c: O.mzone[6], mine: false, seq: 6 } : { c: null, mine: true, seq: 5 }}
  {@const R = M.mzone[6] ? { c: M.mzone[6], mine: true, seq: 6 } : O.mzone[5] ? { c: O.mzone[5], mine: false, seq: 5 } : { c: null, mine: true, seq: 6 }}
  {@const cellW = "calc(var(--zone-side) * var(--df-mzone-size, 1))"}
  <div class="flex" style="gap:var(--df-z-me-zones-gap, var(--df-mzone-gap, var(--zone-gap)));transform:translateX(var(--df-z-me-zones-x, var(--df-mzone-x, 0px)));--zsize:var(--df-mzone-size, 1)">
    <div style="width:{cellW}"></div>
    {@render zoneSquare(L.c ?? null, !L.mine, L.mine, LOCATION.MZONE, L.seq, "EX", true)}
    <div style="width:{cellW}"></div>
    {@render zoneSquare(R.c ?? null, !R.mine, R.mine, LOCATION.MZONE, R.seq, "EX", true)}
    <div style="width:{cellW}"></div>
  </div>
{/snippet}

<!-- One zone ROW (the 5 monster OR 5 spell/trap zones) as its own flex line, so each row carries its
     OWN gap + offset (--df-z-<side>-<mzone|szone>-gap/x/y) for per-zone field alignment. -->
{#snippet zoneRow(p: NonNullable<typeof me>, mine: boolean, kind: "mzone" | "szone")}
  {@const side = mine ? "me" : "opp"}
  {@const loc = kind === "mzone" ? LOCATION.MZONE : LOCATION.SZONE}
  {@const arr = kind === "mzone" ? p.mzone : p.szone}
  {@const order = mine ? [0, 1, 2, 3, 4] : [4, 3, 2, 1, 0]}
  <!-- Each var falls back: per-side override (e.g. Legacy) → unified template var (--df-<kind>-…) →
       default. So a simple template sets --df-mzone-x once for both sides; advanced packs override per
       side. --zsize is read by the card slot inside zoneSquare. -->
  <!-- per-side-per-row vars (--df-z-<side>-<kind>-…, written by the drag helper) sit ABOVE the shared
       per-side (zones-x/gap) and unified (--df-<kind>-…) layers, so each side edits independently. -->
  <div data-fe={kind} data-fe-side={side} class="flex" style="gap:var(--df-z-{side}-{kind}-gap, var(--df-z-{side}-zones-gap, var(--df-{kind}-gap, var(--zone-gap))));transform:translate(var(--df-z-{side}-{kind}-x, var(--df-z-{side}-zones-x, var(--df-{kind}-x, 0px))), var(--df-z-{side}-{kind}-y, var(--df-{kind}-y, 0px)));--zsize:var(--df-z-{side}-{kind}-size, var(--df-{kind}-size, 1))">
    {#each order as i (i)}{@render zoneSquare(arr[i] ?? null, !mine, mine, loc, i)}{/each}
  </div>
{/snippet}

<!-- Monster row nearest the centre line, spell/trap row nearest the player (rows reversed for the
     opponent across the table). The shared Extra Monster Zones live in the dedicated centre bridge,
     never inside one player's half. -->
{#snippet zoneGrid(p: NonNullable<typeof me>, mine: boolean)}
  <div class="flex flex-col items-center gap-[var(--zone-gap)]">
    {#if mine}
      {@render zoneRow(p, true, "mzone")}
      {@render zoneRow(p, true, "szone")}
    {:else}
      {@render zoneRow(p, false, "szone")}
      {@render zoneRow(p, false, "mzone")}
    {/if}
  </div>
{/snippet}

<!-- Wraps a "solo" pile/field cell so a theme can nudge it onto a printed slot, INDEPENDENTLY per side
     ("me"/"opp") — no forced symmetry, so a non-mirrored printed field aligns on both halves. -->
{#snippet solo(side: string, zone: string, content: import("svelte").Snippet, size = 1)}
  <div data-fe={zone} data-fe-side={side} style="transform:translate(var(--df-z-{side}-{zone}-x, var(--df-{zone}-x, 0px)), var(--df-z-{side}-{zone}-y, var(--df-{zone}-y, 0px))) rotate(var(--df-z-{side}-{zone}-rot, var(--df-{zone}-rot, 0deg)));--psize:var(--df-z-{side}-{zone}-size, var(--df-{zone}-size, {size}))">{@render content()}</div>
{/snippet}

<!-- One duelist's field. This follows the 30-slot traditional layout from the approved map: the five
     main columns stay on one shared centre axis, while Field / Extra / GY / Banish / Deck live in their
     own perimeter slots. Those slots are intentionally absolute: they must never widen the central
     grid or push both monster rows apart. -->
{#snippet fieldBlock(p: NonNullable<typeof me>, mine: boolean, col: string)}
  {@const side = mine ? "me" : "opp"}
  <div class="field-block" class:field-block-mine={mine}>
    <div class="field-core">{@render zoneGrid(p, mine)}</div>
    {#if mine}
      <!-- Official player map: Field + Extra left; Graveyard + Deck right. Banish stacks above the
           Graveyard as a non-zone pile, so it never manufactures another board rail. -->
      <div class="field-perimeter field-perimeter-field">
        {#snippet f()}{@render zoneSquare(p.szone[5] ?? null, false, true, LOCATION.SZONE, 5, "FIELD")}{/snippet}{@render solo(side, "field", f)}
      </div>
      <div class="field-perimeter field-perimeter-extra">
        {#snippet e()}{@render pile(p.extraCount, "EXTRA", extraTopCode(p), col, mine, 1, extraPileCards(p.extra))}{/snippet}{@render solo(side, "extra", e)}
      </div>
      <div class="field-perimeter field-perimeter-banish">
        {#snippet b()}{@render pile(p.removed.length, "BANISH", p.removed.at(-1)?.code ?? 0, col, mine, 1, p.removed)}{/snippet}{@render solo(side, "banish", b)}
      </div>
      <div class="field-perimeter field-perimeter-gy">
        {#snippet g()}{@render pile(p.grave.length, "GY", p.grave.at(-1)?.code ?? 0, col, mine, 1, p.grave)}{/snippet}{@render solo(side, "gy", g)}
      </div>
      <div class="field-perimeter field-perimeter-deck">
        {#snippet d()}{@render pile(p.deckCount, "DECK", 0, col, mine)}{/snippet}{@render solo(side, "deck", d)}
      </div>
    {:else}
      <!-- Exact 180° map for the opponent. Their main columns stay centred with yours; only the
           surrounding special slots mirror across the table. -->
      <div class="field-perimeter field-perimeter-field">
        {#snippet f()}{@render zoneSquare(p.szone[5] ?? null, true, false, LOCATION.SZONE, 5, "FIELD")}{/snippet}{@render solo(side, "field", f)}
      </div>
      <div class="field-perimeter field-perimeter-extra">
        {#snippet e()}{@render pile(p.extraCount, "EXTRA", extraTopCode(p), col, mine, 1, extraPileCards(p.extra))}{/snippet}{@render solo(side, "extra", e)}
      </div>
      <div class="field-perimeter field-perimeter-banish">
        {#snippet b()}{@render pile(p.removed.length, "BANISH", p.removed.at(-1)?.code ?? 0, col, mine, 1, p.removed)}{/snippet}{@render solo(side, "banish", b)}
      </div>
      <div class="field-perimeter field-perimeter-gy">
        {#snippet g()}{@render pile(p.grave.length, "GY", p.grave.at(-1)?.code ?? 0, col, mine, 1, p.grave)}{/snippet}{@render solo(side, "gy", g)}
      </div>
      <div class="field-perimeter field-perimeter-deck">
        {#snippet d()}{@render pile(p.deckCount, "DECK", 0, col, mine)}{/snippet}{@render solo(side, "deck", d)}
      </div>
    {/if}
  </div>
{/snippet}

<!-- Compact duelist HUD: an identity seal, owner-colour energy bar, and readable LP. It belongs to
     the same cyan/magenta VRAINS system as the rest of the client — not a detached retro health bar. -->
{#snippet lpChip(name: string, life: number, deckN: number, handN: number, col: string, av: { look: ReturnType<typeof chibiFrom> | null; bust: NpcBust | null }, right = false)}
  <div class="duelist-hud flex w-[clamp(12rem,18vw,15.75rem)] items-center gap-2 {right ? 'flex-row-reverse text-right' : ''}">
    <div class="grid size-10 shrink-0 place-items-center overflow-hidden chamfer bg-void/92" style="--c:6px;box-shadow:inset 0 0 0 1.25px color-mix(in oklab,{col} 72%,transparent),0 0 16px color-mix(in oklab,{col} 24%,transparent)">
      {#if av.bust}
        <NpcBustView bust={av.bust} height={42} windowed />
      {:else if av.look}
        <PortraitPreview size={42} look={av.look} windowed />
      {:else}
        <span class="font-display text-base font-bold" style="color:{col}">{name.slice(0, 1).toUpperCase()}</span>
      {/if}
    </div>
    <div class="min-w-0 flex-1">
      <div class="flex items-baseline gap-2 {right ? 'flex-row-reverse' : ''}">
        <span class="truncate font-display text-[0.7rem] font-bold uppercase tracking-[0.12em] text-ink">{name}</span>
        <span class="shrink-0 font-display text-[0.52rem] uppercase tracking-[0.09em] tabular-nums text-ink-dim/85">H{handN} · D{deckN}</span>
      </div>
      <div class="relative mt-1 h-[1.15rem] overflow-hidden chamfer bg-void/94" style="--c:4px;box-shadow:inset 0 0 0 1.25px color-mix(in oklab,{col} 58%,transparent),0 0 12px color-mix(in oklab,{col} 16%,transparent)">
        <div class="absolute inset-y-[2px] left-[2px] chamfer transition-[width] duration-500" style="--c:2px;width:calc({lp(life)}% - 4px);background:{lpFill(life, col)};box-shadow:0 0 9px color-mix(in oklab,{col} 42%,transparent)"></div>
        <span class="duelist-lp absolute inset-0 grid place-items-center font-display text-[0.94rem] font-extrabold tabular-nums leading-none">{life}</span>
      </div>
    </div>
  </div>
{/snippet}

<!-- viewport sizer (observed). Transparent so the live CyberBackground (behind, App-level) shows
     through, the mat is de-slabbed. --ui scales the OUTSIDE-stage dialogs gently with viewport. -->
<div bind:this={sizer} class="duel-sizer fixed inset-0 overflow-hidden" style="--ui:{hudUi};--info-w:{INFO_W}px">
  {#if !b || !me || !opp}
    <!-- Pre-duel gate: connect, then WARM THE CARD ART CACHE before the first beat plays — the duel
         never starts with JPEGs still trickling in. Progress counts the opening's images. -->
    <div class="grid h-full place-items-center">
      <div class="flex w-64 flex-col items-center gap-4" in:fade>
        <div class="size-8 animate-spin rounded-full border-2 border-cyan/30 border-t-cyan"></div>
        <span class="font-display text-sm uppercase tracking-[0.3em] text-cyan-dim">
          {duel.loading && duel.loadProgress.total > 0 ? "Preparing duel…" : "Connecting to duel…"}
        </span>
        {#if duel.loading && duel.loadProgress.total > 0}
          <div class="h-1 w-full overflow-hidden rounded-full bg-void-2/90">
            <div class="h-full rounded-full bg-cyan transition-[width] duration-200" style="width:{Math.round((duel.loadProgress.done / duel.loadProgress.total) * 100)}%;box-shadow:0 0 10px var(--color-cyan)"></div>
          </div>
          <span class="font-display text-[0.62rem] uppercase tracking-[0.24em] text-cyan-dim/80">Loading cards · {duel.loadProgress.done}/{duel.loadProgress.total}</span>
        {/if}
      </div>
    </div>
  {:else}
    <!-- ══ card-info rail, OUTSIDE the stage: a real, full-VIEWPORT-height column that squishes the
         aspect-locked board to its right (true size, never letterboxed). ══ -->
    {#if INFO_W > 0}
      <!-- raised above the pile viewer (z-58/59) while it's open, so hovering a GY/banished card
           shows its preview here instead of behind the dimmed backdrop -->
      <aside class="tp-cardinfo fixed left-0 top-0 bottom-0 flex flex-col border-r border-cyan/15 bg-void/55 backdrop-blur-sm {duel.pileView ? 'z-[60]' : 'z-30'}" style="width:var(--info-w)">
        <CardInfoRail card={hovered} info={hoverInfo} />
        <!-- comms docked as its own bordered section at the foot of the rail -->
        <DuelChat docked />
      </aside>
    {/if}

    <!-- ══ Dismissible card detail — an on-demand inspector that never resizes the duel field. ══ -->
    {#if selectedCard}
      <aside
        use:draggableDialog={{ gutter: 8 }}
        aria-label="Card details"
        class="tp-cardinfo df-carddetail fixed left-3 top-[4.4rem] bottom-auto flex h-auto max-h-[min(27rem,calc(100dvh-5.4rem))] w-[clamp(12rem,16vw,15rem)] flex-col overflow-hidden chamfer border border-cyan/30 bg-void/92 backdrop-blur-md phone:left-2 phone:top-12 phone:bottom-auto phone:h-auto phone:max-h-[min(16rem,calc(100dvh-4rem))] phone:w-[min(19rem,52vw)]"
        style="--c:10px;z-index:{duel.pileView ? 61 : 55};box-shadow:0 0 0 1px color-mix(in oklab,var(--color-cyan) 18%,transparent),0 18px 50px rgba(0,0,0,.6)"
        transition:fly={{ x: -18, duration: 180 }}
      >
        <div
          data-dialog-drag-handle
          class="dialog-drag-handle flex min-h-10 shrink-0 items-center justify-between border-b border-cyan/18 bg-void/38 px-3 py-2 touch:min-h-11"
          title="Drag card details"
        >
          <span class="pointer-events-none flex items-center gap-2 font-display text-[0.58rem] font-bold uppercase tracking-[0.17em] text-cyan-dim">
            <svg viewBox="0 0 12 18" class="h-3.5 w-2.5 text-cyan/55" fill="currentColor" aria-hidden="true"><circle cx="3" cy="3" r="1.25" /><circle cx="9" cy="3" r="1.25" /><circle cx="3" cy="9" r="1.25" /><circle cx="9" cy="9" r="1.25" /><circle cx="3" cy="15" r="1.25" /><circle cx="9" cy="15" r="1.25" /></svg>
            Card details
          </span>
          <button type="button" class="grid size-7 place-items-center chamfer-btn text-ink-dim transition-colors hover:bg-magenta/15 hover:text-magenta touch:size-9" style="--c:3px" aria-label="Close card details" onclick={() => (selectedCard = null)}>
            <svg viewBox="0 0 24 24" class="size-3.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
          </button>
        </div>
        <CardInfoRail card={selectedCard} info={selectedInfo} />
      </aside>
    {/if}

    <!-- ══ the fixed LOGICAL stage, FIELD + HANDS ONLY, aspect-locked, scaled into the space the
         card-info rail leaves. Chrome (LP / phase / log) rides outside it. ══ -->
    <!-- The stage is the aspect-locked, letterboxed 2D playmat. In 3D the arena self-fits its own field
         (fitCamera) and the hand fan is camera-anchored, so the stage goes FULL-VIEWPORT — the canvas
         fills the real screen and the hand docks to the true bottom edge at every window size (no
         letterbox gap that would float the hand up / vary the crop). The invisible DOM hand anchors
         still live here for the 2D board; in 3D they're hidden and unused. -->
    <div
      class="duel-stage absolute left-0 top-0 origin-top-left"
      class:df-emz={duel.format === "free"}
      class:df-no-emz={duel.format !== "free"}
      style="width:{use3d ? vw : BASE_W}px;height:{use3d ? vh : BASE_H}px;transform:{use3d ? 'none' : `translate(${offX}px,${offY}px) scale(${stageScale})`};
             --ch:calc(96px * var(--card-scale,1));--cw:calc(var(--ch) * 59 / 86);--gap:6px;
             --zone-side:clamp(88px,calc(var(--ch) * 1.06),116px);--zone-track:var(--zone-side);--zone-gap:clamp(4px,.5vw,8px);--main-grid-w:calc(var(--zone-side) * 5 + var(--zone-gap) * 4);
             --handw:calc(var(--cw) * {use3d ? 1.5 : 1.35});--ohandw:calc(var(--cw) * {use3d ? 0.98 : 0.86});
             --ohand:calc(var(--ch) * {use3d ? 0.96 : 0.84});--mhand:calc(var(--ch) * {use3d ? 1.22 : 1.18});--ui:1;
             --handnudge:0px"
    >
      <!-- ══ opponent hand, flat 2D, sitting JUST ABOVE their board ══ -->
      <!-- nudge left onto the zones: the field's far-right BANISH column pulls the 5 zones off stage-centre,
           so the hand (which centres on the stage) must shift by the same amount to sit over them. 2D only,
           the 3D arena pins its zones to centre, so the hand already lines up there. -->
      <!-- 3D: invisible measurement anchor (the visible opponent hand is the WebGL fan). 2D: real row. -->
      <div data-mref-hand="opp" data-fe="hand" data-fe-side="opp" class="tp-hand absolute left-0 right-0 top-0 z-20 flex items-end justify-center px-3 pb-1 {use3d ? 'opacity-0 pointer-events-none' : ''}" class:flat-opp-hand-clearance={!tilted} style="height:var(--ohand);gap:var(--df-z-opp-hand-gap, var(--df-hand-gap, var(--gap)));transform-origin:top center;transform:{use3d ? 'none' : 'translate(calc(var(--handnudge) + var(--df-z-opp-hand-x, var(--df-hand-x, 0px))),var(--df-z-opp-hand-y, var(--df-hand-y, 0px))) scale(var(--df-z-opp-hand-size, var(--df-hand-size, 1)))'}">
        {#each handKeyed(opp.hand) as it, i (it.k)}
          {@const c = it.c}
          {@const cnum = c && c.location ? chainAt(c.location, c.controller, c.sequence) : null}
          {@const off = i - (opp.hand.length - 1) / 2}
          <!-- Build the top semicircle directly: the quadratic offset keeps the correct arc, while
               the signed fan angle alone turns each card. No blanket 180° flip, so card backs and
               revealed cards remain upright to the viewer. -->
          <div class="opp-hand-card relative" style="width:var(--ohandw);margin-inline:calc(var(--ohandw) * -0.09);visibility:{handHidden('opp', i) ? 'hidden' : 'visible'};rotate:{off * 5}deg;translate:0 {off * off * 3}px;transform-origin:50% 50%" in:fly={{ y: -22, duration: 220 }} animate:flip={{ duration: 240, easing: quintOut }}>{@render cell(c, "")}{#if cnum}{@render chainBadge(cnum)}{/if}</div>
        {/each}
      </div>

      {#if use3d}
        <!-- Parked WebGL diagnostic path. The production renderer below is the reliable CSS perspective
             board; this remains available only behind ?force3d=1. -->
        <div class="absolute inset-0 z-10">
          <DuelBoard3D
            me={me}
            opp={opp}
            {seat}
            {prompt}
            cardState={cardStateFor}
            foils={myFoils}
            links={linkGroup}
            sleeve={mySleeve}
            oppSleeve={duel.oppSleeve || undefined}
            onhover={(c) => { linkAnchor = c; }}
            onclick={(c, x, y) => clickCard(c, { clientX: x, clientY: y } as MouseEvent)}
            onbackdrop={() => (selectedCard = null)}
            oninitfail={arenaInitFailed}
          />
        </div>
      {:else}
        <!-- ══ duelist busts flanking the mat (split-screen), behind the translucent board, 2D only ══ -->
        <!-- Both duelists use the same canonical left-facing artwork. The player on the left is
             mirrored by the renderer so both figures face inward across the mat. -->
        <div class="duel-bust duel-bust-me pointer-events-none absolute bottom-0 left-0 z-[7]" style="margin-left:{myAvBleed - edgePush}px">
          <PortraitPreview size={avSize} look={myLook} mirrored blink />
        </div>
        {#if oppBust}
          <!-- An NPC bust is cropped TIGHT to the figure (no square padding like the PvP portrait), so
               it needs a deeper bleed to hug the right edge instead of crowding the field. -->
          <div class="duel-bust duel-bust-opp pointer-events-none absolute bottom-0 right-0 z-[7]" style="margin-right:{npcBleed - edgePush}px">
            <NpcBustView bust={oppBust} height={avSize} />
          </div>
        {:else if oppLook}
          <div class="duel-bust duel-bust-opp pointer-events-none absolute bottom-0 right-0 z-[7]" style="margin-right:{oppAvBleed - edgePush}px">
            <PortraitPreview size={avSize} look={oppLook} blink />
          </div>
        {:else}
          <div class="pointer-events-none absolute bottom-0 right-0 z-[7] grid place-items-center" style="width:{avSize}px;height:{avSize}px;margin-right:{oppAvBleed - edgePush}px">
            <span class="font-display text-[7rem] font-bold text-magenta/25" style="text-shadow:0 0 40px var(--color-magenta)">{duel.oppName.slice(0, 1).toUpperCase()}</span>
          </div>
        {/if}

        <!-- ══ the DOM board — only gameplay geometry rides this perspective plane by default. The
             live WebGPU CyberBackground remains the arena; .tp-field exists solely for an opted-in
             texture-pack image. ══ -->
        <div class="absolute left-0 right-0 z-10 flex items-center justify-center px-3" class:mat3d-stage={tilted} style="top:calc(var(--ohand) - 26px);bottom:var(--mhand)">
          <div
            use:projectedFieldInteraction
            class="duel-plane relative grid place-items-center"
            class:mat3d={tilted}
            class:shake={attacking}
          >
            <div class="tp-field pointer-events-none absolute inset-0" style="background:var(--df-field,transparent)"></div>
            <div class="tp-mat duel-mat-flat relative z-10 flex flex-col items-center gap-0 px-[clamp(0.7rem,1.4vw,1.4rem)] py-[clamp(0.6rem,1.2vh,1.1rem)]">
              {@render fieldBlock(opp, false, "var(--color-magenta)")}
              <div class="field-bridge flex w-full items-center justify-center" class:field-bridge-link={duel.format === "free"}>
                {#if duel.format === "free"}
                  {@render emzRow()}
                {/if}
              </div>
              {@render fieldBlock(me, true, "var(--color-cyan)")}
            </div>
          </div>
        </div>
      {/if}

      <!-- ══ your hand, flat 2D, pinned to the bottom (lifts on hover) ══ -->
      <!-- same BANISH-asymmetry nudge as the opponent hand, so your cards sit over your zones (2D only). -->
      <!-- 3D board: the visible, interactive hand is the WebGL fan (DuelArena). This DOM row stays as an
           INVISIBLE, non-interactive measurement anchor so the motion layer (draw / bounce-to-hand) can
           still resolve hand screen positions via `data-mref-hand`. 2D keeps the real flat hand. -->
      <!-- data-sfx="hover-off": hand cards CLICK-blip only — sweeping the fan must not tick per card -->
      <div data-mref-hand="me" data-fe="hand" data-fe-side="me" data-sfx="hover-off" class="tp-hand absolute left-0 right-0 bottom-0 z-20 flex items-end justify-center px-3 pb-2 {use3d ? 'opacity-0 pointer-events-none' : ''}" style="height:var(--mhand);gap:var(--df-z-me-hand-gap, var(--df-hand-gap, var(--gap)));transform-origin:bottom center;transform:{use3d ? 'none' : 'translate(calc(var(--handnudge) + var(--df-z-me-hand-x, var(--df-hand-x, 0px))),var(--df-z-me-hand-y, var(--df-hand-y, 0px))) scale(var(--df-z-me-hand-size, var(--df-hand-size, 1)))'}">
        {#each handKeyed(me.hand) as it, i (it.k)}
          {@const c = it.c}
          {@const cnum = c && c.location ? chainAt(c.location, c.controller, c.sequence) : null}
          {@const off = i - (me.hand.length - 1) / 2}
          <!-- the held-out FAN (MD-style): each card rolls around a pivot below the hand — centre card
               highest, edges droop + tilt outward. Slight overlap via -margin. Individual
               rotate/translate props, so the hover lift + animate:flip's transform compose cleanly. -->
          <div class="relative" style="width:var(--handw);margin-inline:calc(var(--handw) * -0.09);visibility:{handHidden('me', i) ? 'hidden' : 'visible'};rotate:{off * 4}deg;translate:0 {off * off * 2.6}px;transform-origin:50% 130%" in:fly={{ y: 30, duration: 240 }} animate:flip={{ duration: 240, easing: quintOut }}>
            <!-- the raise is CLICK-driven (the selected/menued card), never hover — a pointer sweep
                 across the fan must not bounce cards (owner report). Tap-away drops it back. -->
            <div class="duel-hand-lift relative origin-bottom transition-transform duration-150" class:hand-raised={menu?.card === c || selectedCard === c}>{@render cell(c, "")}{#if cnum}{@render chainBadge(cnum)}{/if}</div>
          </div>
        {/each}
      </div>

      <!-- ══ confirm-cards reveal ══ -->
      {#if duel.reveal}
        <div class="pointer-events-none absolute left-1/2 top-[22%] z-40 flex -translate-x-1/2 flex-col items-center gap-1.5" in:scale={{ duration: 220, start: 0.85 }} out:fade={{ duration: 200 }}>
          <span class="chamfer bg-void/85 px-3 py-1 font-display text-[0.6rem] font-bold uppercase tracking-[0.18em] text-cyan backdrop-blur" style="--c:4px;text-shadow:0 0 10px var(--color-cyan)">Revealed</span>
          <div class="flex gap-2">
            {#each duel.reveal.cards.slice(0, 6) as code (code)}
              <img src={cardImageLarge(code)} alt="revealed card" class="w-[84px] chamfer ring-1 ring-cyan/45" style="--c:6px;box-shadow:0 0 18px color-mix(in oklab,var(--color-cyan) 22%,transparent)" draggable="false" />
            {/each}
          </div>
        </div>
      {/if}

      <!-- ══ floating FX: attack beams · damage numbers · turn toasts ══ -->
      <div class="pointer-events-none absolute inset-0 z-40 overflow-hidden">
        {#each duel.fx as f (f.id)}
          {#if f.kind === "toast"}
            <div class="fx-toast absolute left-1/2 top-[38%]">{f.text}</div>
          {:else if f.kind === "attack"}
            <div class="fx-beam absolute left-1/2 top-1/2" style="color:{f.side === 'me' ? 'var(--color-cyan)' : 'var(--color-magenta)'};--dir:{f.side === 'me' ? -1 : 1}"></div>
          {:else if f.kind === "damage" || f.kind === "recover"}
            <div class="fx-num absolute left-1/2" style="top:{f.side === 'opp' ? '26%' : '60%'};color:{f.kind === 'recover' ? 'var(--color-lime)' : '#ff3b54'}">
              {f.kind === "recover" ? "+" : "-"}{f.amount}
            </div>
          {/if}
        {/each}
      </div>
    </div>

    <!-- ══════ OVERLAYS (outside the scaled stage → true viewport size, always prominent) ══════ -->

    <!-- Card-motion layer: flies cards / shatter shards across the board (draw, send-to-GY, banish,
         bounce, destroy) in BOTH renderers. Lives OUTSIDE the transformed stage so its fixed
         positioning is viewport-relative. -->
    <DuelMotionLayer />
    <!-- Held attacker→defender relationship. Unlike impact FX, this survives response/chain windows
         so both players always know exactly which declaration they are answering. -->
    <AttackDeclaration {nameOf} />
    <!-- Screen-space ATK/DEF + level labels: flat 2D text glued to each monster's PROJECTED rect —
         outside the perspective plane, so they never shear, shrink or depth-sort behind cards. -->
    {#if !use3d}
      <DuelStatLabels />
    {/if}
    <!-- screen-space cinematics (cut-ins, phase sweep, lethal vignette) — both renderers -->
    <DuelCinematics />


    <!-- ══ Compact VRAINS HUD: player identities flank a concise turn status without consuming the
         perspective field. ══ -->
    <div data-duelist={seat} class="fixed left-3 top-2 z-30 origin-top-left" style="scale:calc(var(--ui) * var(--lp-mult, 1))">
      {@render lpChip("You", me.lp, me.deckCount, me.hand.length, "var(--color-cyan)", { look: myLook, bust: null })}
    </div>
    <div data-duelist={1 - seat} class="fixed right-3 top-2 z-30 flex origin-top-right flex-col items-end gap-1.5" style="scale:calc(var(--ui) * var(--lp-mult, 1))">
      {@render lpChip(duel.oppName, opp.lp, opp.deckCount, opp.hand.length, "var(--color-magenta)", { look: oppLook, bust: oppBust }, true)}
    </div>

    <!-- centre slot: turn ownership at a glance. The phase orb owns the countdown, avoiding duplicate
         timer chrome in the most valuable strip of the field. -->
    <div class="fixed left-1/2 top-2 z-30 flex origin-top -translate-x-1/2 flex-col items-center gap-1" style="scale:calc(var(--ui) * var(--lp-mult, 1))">
      <div class="flex items-center gap-2 chamfer bg-void/90 px-3 py-1 backdrop-blur" style="--c:5px;box-shadow:inset 0 0 0 1px color-mix(in oklab,{myTurn ? 'var(--color-cyan)' : 'var(--color-magenta)'} 45%,transparent)">
        <span class="font-display text-[0.54rem] font-bold uppercase tracking-[0.15em] text-ink-dim">Turn {turnNo}</span>
        <span class="max-w-24 truncate font-display text-[0.6rem] font-extrabold uppercase tracking-[0.12em] {myTurn ? 'text-cyan' : 'text-magenta'}">{myTurn ? "Your turn" : duel.oppName}</span>
      </div>
      {#if duel.bestOf === 3}
        <!-- Match scoreboard (Bo3 only), tucked under the timer slot. -->
        <div class="flex items-center gap-1.5 chamfer border border-cyan-dim/50 bg-void/70 px-2.5 py-1 backdrop-blur" style="--c:4px" title="Match score · best of 3">
          <span class="font-display text-[0.56rem] font-bold uppercase tracking-[0.18em] text-cyan-dim">Match</span>
          <span class="font-display text-[0.6rem] font-bold tracking-[0.06em] tabular-nums">
            <span class="text-cyan">{duel.matchScore.you}</span>
            <span class="text-ink-dim/60">·</span>
            <span class="text-magenta">{duel.matchScore.opp}</span>
          </span>
          <span class="ml-1 text-[0.55rem] font-display uppercase tracking-[0.12em] text-ink-dim/70">G{duel.gameNo}/3</span>
        </div>
      {/if}
    </div>

    <!-- ══ phase control, just the orb, upper-right under the opponent's LP bar ══
         Phones: bottom-right instead — the 390px column can't fit LP + orb + toggles, so the
         toggles collapse behind a pill and the orb takes the freed thumb corner. -->
    <div class="df-phase fixed right-3 top-[4rem] z-30 flex items-center justify-end phone:top-auto phone:bottom-3">
      <PhaseControl
        turn={turnNo}
        phaseMask={b.phase}
        {myTurn}
        {timerPct}
        {timerSecs}
        timerMine={timerOwnerMine}
        timerActive={duel.timerActivePlayer !== null}
        timerEnabled={duel.timerLimitMs > 0}
        {advances}
        onadvance={(choice) => sendChoice(choice as CardAction["choice"])}
      />
    </div>

    <!-- ══ Duel options — set-once controls stay off the field until explicitly requested. The same
         compact trigger is used on desktop and phone; phones offset it left of the phase orb. ══ -->
    <!-- GAMEPLAY-CRITICAL: the full-viewport close backdrop sits in its OWN no-transition block, and
         the panel deliberately has NO out-transition. An outro ANYWHERE in an {#if} block keeps the
         whole block mounted until it finishes, and Svelte 5 permanently orphans an interrupted outro
         (open→close→open during the fade): this exact backdrop got stuck invisible at z-38 eating
         EVERY click — cards, back row, even the phase orb — and a player timed out of a real duel.
         Never attach an outro to (or co-mount one with) a click-catching overlay. -->
    {#if togglesOpen}
      <button class="fixed inset-0 z-[38] cursor-default" aria-label="Close duel options" onclick={() => (togglesOpen = false)}></button>
    {/if}
    {#if togglesOpen}
      <div
        id="duel-options-menu"
        role="group"
        aria-label="Duel options"
        class="df-autotoggles df-options-menu fixed bottom-[3.6rem] right-4 z-40 flex max-h-[calc(100dvh-4.5rem)] w-[13.5rem] flex-col items-stretch gap-1.5 overflow-y-auto scroll-cyber chamfer border border-cyan/28 bg-void/96 p-2 shadow-[0_14px_42px_rgba(0,0,0,.58)]"
        style="--c:9px"
        in:scale={{ duration: 150, start: 0.96 }}
      >
        <div class="flex items-center justify-between border-b border-cyan/15 px-1.5 pb-1.5">
          <span class="font-display text-[0.64rem] font-bold uppercase tracking-[0.2em] text-cyan">Duel Options</span>
          <span class="font-display text-[0.52rem] uppercase tracking-[0.12em] text-ink-dim/55"><span class="touch:hidden">Esc to close</span><span class="hidden touch:inline">Tap outside</span></span>
        </div>
      <button
        type="button"
        onclick={toggleAutoPass}
        title={(duel.autoPass
          ? "Auto: optional response/chain windows pass automatically. Click to stop at each window so you can chain."
          : "Manual: you're asked at every response window so you can chain / hold priority. Click for auto.")
          + " Tip: hold right-click to skip every response window while held."}
        class="flex items-center justify-center gap-1.5 chamfer-btn touch:min-h-11 touch:gap-2 border bg-void/70 font-display font-bold uppercase tracking-[0.12em] backdrop-blur transition-colors {duel.autoPass ? 'border-lime/55 text-lime hover:bg-lime/12' : 'border-gold/55 text-gold hover:bg-gold/12'}"
        style="--c:4px;font-size:calc(0.6rem*var(--ui));padding:calc(0.3rem*var(--ui)) calc(0.7rem*var(--ui))"
      >
        <span class="grid place-items-center rounded-full {duel.autoPass ? 'bg-lime/80' : 'bg-gold/80'}" style="width:calc(0.6rem*var(--ui));height:calc(0.6rem*var(--ui));box-shadow:0 0 6px currentColor"></span>
        {duel.autoPass ? "Auto ⏩" : "Manual ✋"}
      </button>
      <button
        type="button"
        onclick={toggleResponses}
        title={duel.responsesOff
          ? "Responses off: optional chain / effect windows are skipped automatically. Click to be asked again. (Or hold right-click to skip momentarily.)"
          : "Responses on: you're asked at each optional chain / effect window. Click to skip them all automatically. (Or hold right-click to skip momentarily.)"}
        class="flex items-center justify-center gap-1.5 chamfer-btn touch:min-h-11 touch:gap-2 border bg-void/70 font-display font-bold uppercase tracking-[0.12em] backdrop-blur transition-colors {duel.responsesOff ? 'border-lime/55 text-lime hover:bg-lime/12' : 'border-ink-dim/40 text-ink-dim/70 hover:bg-cyan/10'}"
        style="--c:4px;font-size:calc(0.6rem*var(--ui));padding:calc(0.3rem*var(--ui)) calc(0.7rem*var(--ui))"
      >
        <span class="grid place-items-center rounded-full {duel.responsesOff ? 'bg-lime/80' : 'bg-ink-dim/55'}" style="width:calc(0.6rem*var(--ui));height:calc(0.6rem*var(--ui));box-shadow:0 0 6px currentColor"></span>
        {duel.responsesOff ? "Response Off ⏩" : "Response On"}
      </button>
      <button
        type="button"
        onclick={toggleAutoZone}
        title={duel.autoZone
          ? "Auto-zone: cards auto-place center-out. Click to choose zones yourself."
          : "Pick zone: click a glowing legal zone to place. Click for auto placement."}
        class="flex items-center justify-center gap-1.5 chamfer-btn touch:min-h-11 touch:gap-2 border bg-void/70 font-display font-bold uppercase tracking-[0.12em] backdrop-blur transition-colors {duel.autoZone ? 'border-cyan/45 text-cyan-dim hover:bg-cyan/12' : 'border-cyan/70 text-cyan hover:bg-cyan/12'}"
        style="--c:4px;font-size:calc(0.6rem*var(--ui));padding:calc(0.3rem*var(--ui)) calc(0.7rem*var(--ui))"
      >
        <span class="grid place-items-center rounded-full {duel.autoZone ? 'bg-cyan-dim/70' : 'bg-cyan/90'}" style="width:calc(0.6rem*var(--ui));height:calc(0.6rem*var(--ui));box-shadow:0 0 6px currentColor"></span>
        {duel.autoZone ? "Zone Auto" : "Pick Zone"}
      </button>
      <button
        type="button"
        onclick={toggleProMode}
        title="No hints — you must know what your cards can do. Illegal picks are called out."
        class="flex items-center justify-center gap-1.5 chamfer-btn touch:min-h-11 touch:gap-2 border bg-void/70 font-display font-bold uppercase tracking-[0.12em] backdrop-blur transition-colors {duel.proMode ? 'border-gold/60 text-gold hover:bg-gold/12' : 'border-ink-dim/40 text-ink-dim/70 hover:bg-gold/10'}"
        style="--c:4px;font-size:calc(0.6rem*var(--ui));padding:calc(0.3rem*var(--ui)) calc(0.7rem*var(--ui))"
      >
        <span class="grid place-items-center rounded-full {duel.proMode ? 'bg-gold/90' : 'bg-ink-dim/55'}" style="width:calc(0.6rem*var(--ui));height:calc(0.6rem*var(--ui));box-shadow:0 0 6px currentColor"></span>
        {duel.proMode ? "Pro Mode ◆" : "Pro Mode"}
      </button>
      {#if !use3d && !duel.preview}
        <button
          type="button"
          onclick={() => { toggle3d(); togglesOpen = false; }}
          title={tilted ? "Perspective board. Click for the top-down board." : "Top-down board. Click for the perspective board."}
          class="flex items-center justify-center gap-1.5 chamfer-btn touch:min-h-11 touch:gap-2 border bg-void/70 font-display font-bold uppercase tracking-[0.12em] backdrop-blur transition-colors {tilted ? 'border-magenta/60 text-magenta hover:bg-magenta/12' : 'border-magenta/40 text-magenta/65 hover:bg-magenta/12'}"
          style="--c:4px;font-size:calc(0.6rem*var(--ui));padding:calc(0.3rem*var(--ui)) calc(0.7rem*var(--ui))"
        >
          <span class="grid place-items-center rounded-full {tilted ? 'bg-magenta/90' : 'bg-magenta/55'}" style="width:calc(0.6rem*var(--ui));height:calc(0.6rem*var(--ui));box-shadow:0 0 6px currentColor"></span>
          {tilted ? "Perspective" : "Top-down"}
        </button>
      {/if}
      <button
        type="button"
        onclick={cycleDuelSpeed}
        title="Duel animation speed (scales every beat and animation together)"
        class="flex items-center justify-center gap-1.5 chamfer-btn touch:min-h-11 touch:gap-2 border bg-void/70 font-display font-bold uppercase tracking-[0.12em] backdrop-blur transition-colors {settings.duelSpeed !== 1 ? 'border-lime/60 text-lime hover:bg-lime/12' : 'border-ink-dim/40 text-ink-dim/70 hover:bg-lime/10'}"
        style="--c:4px;font-size:calc(0.6rem*var(--ui));padding:calc(0.3rem*var(--ui)) calc(0.7rem*var(--ui))"
      >
        <span class="grid place-items-center rounded-full {settings.duelSpeed !== 1 ? 'bg-lime/90' : 'bg-ink-dim/55'}" style="width:calc(0.6rem*var(--ui));height:calc(0.6rem*var(--ui));box-shadow:0 0 6px currentColor"></span>
        Speed ×{settings.duelSpeed}
      </button>
      {#if !duel.replayMode && !duel.ended}
        <button
          type="button"
          onclick={() => { togglesOpen = false; duel.askSurrender = true; }}
          title="Concede this duel"
          class="flex items-center justify-center gap-1.5 chamfer-btn touch:min-h-11 touch:gap-2 border border-magenta/42 bg-void/70 font-display font-bold uppercase tracking-[0.12em] text-magenta/85 backdrop-blur transition-colors hover:border-magenta/75 hover:bg-magenta/12 hover:text-magenta"
          style="--c:4px;font-size:calc(0.6rem*var(--ui));padding:calc(0.3rem*var(--ui)) calc(0.7rem*var(--ui))"
        >
          Surrender
        </button>
      {/if}
      </div>
    {/if}

    <!-- One compact trigger is the only persistent options chrome on the field. -->
      <button
        type="button"
        onclick={() => (togglesOpen = !togglesOpen)}
        class="df-options-trigger fixed bottom-3 right-4 z-40 flex touch:min-h-11 items-center justify-center gap-1.5 chamfer-btn border bg-void/88 px-3.5 py-2.5 font-display text-[0.66rem] font-bold uppercase tracking-[0.12em] transition-colors {togglesOpen ? 'border-cyan/75 text-cyan' : 'border-cyan-dim/55 text-ink-dim hover:border-cyan/55 hover:text-cyan'}"
        style="--c:4px;box-shadow:0 8px 24px rgba(0,0,0,.4)"
        aria-expanded={togglesOpen}
        aria-controls="duel-options-menu"
      >
        <svg viewBox="0 0 24 24" class="size-3.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" /></svg>
        Options
        <span class="text-[0.48rem] transition-transform {togglesOpen ? 'rotate-180' : ''}" aria-hidden="true">▲</span>
      </button>

    <!-- ══ Pro Mode rejection pills: an illegal pick sent nothing — say so, clearly but gently, near
         where the player is acting. Above the menus/pile viewer (z-61) so a rejection always reads. ══ -->
    {#if duel.fx.some((f) => f.kind === "notice")}
      <div class="pointer-events-none fixed right-0 z-[62] flex flex-col items-center gap-1.5" style="left:var(--info-w);bottom:24%">
        {#each duel.fx as f (f.id)}
          {#if f.kind === "notice"}
            <div class="chamfer bg-void/92 px-4 py-2 font-display text-[0.74rem] font-bold uppercase tracking-[0.1em] text-gold backdrop-blur"
                 style="--c:6px;box-shadow:0 0 0 1px color-mix(in oklab,var(--color-gold) 55%,transparent),0 0 18px color-mix(in oklab,var(--color-gold) 22%,transparent)"
                 in:fly={{ y: 8, duration: 150 }} out:fade={{ duration: 180 }}>{f.text}</div>
          {/if}
        {/each}
      </div>
    {/if}

    <!-- ══ hold-right-click skip indicator (shown only while held) ══ -->
    {#if duel.skipHeld}
      <div class="pointer-events-none fixed bottom-3 left-1/2 z-[58] -translate-x-1/2" transition:fade={{ duration: 100 }}>
        <div class="flex items-center gap-1.5 chamfer bg-void/85 px-3 py-1.5 backdrop-blur" style="--c:5px;box-shadow:0 0 0 1px color-mix(in oklab,var(--color-lime) 50%,transparent),0 0 16px color-mix(in oklab,var(--color-lime) 26%,transparent)">
          <span class="font-display text-[0.66rem] font-bold uppercase tracking-[0.18em] text-lime" style="text-shadow:0 0 10px var(--color-lime)">⏩ Skipping responses</span>
        </div>
      </div>
    {/if}

    <!-- (the play log now lives as a tab inside the bottom-left chat, see DuelChat) -->

    <!-- ══ chain-depth badge, persistent while a chain is building (EdoPro's chain-link counter) ══ -->
    {#if duel.chainDepth > 0}
      <!-- centred on the FIELD (right of the info rail), like the hand, not the raw viewport -->
      <div class="pointer-events-none fixed top-[12%] right-0 z-[56] flex justify-center" style="left:var(--info-w)">
        <div class="flex items-center gap-2 chamfer bg-void/85 px-3 py-1 backdrop-blur" style="--c:5px;box-shadow:0 0 0 1px color-mix(in oklab,var(--color-gold) 50%,transparent),0 0 18px color-mix(in oklab,var(--color-gold) 26%,transparent)" in:fly={{ y: -10, duration: 160 }} out:fade={{ duration: 160 }}>
          <span class="text-[0.8rem]">⛓</span>
          <span class="font-display text-[0.66rem] font-bold uppercase tracking-[0.22em] text-gold" style="text-shadow:0 0 10px var(--color-gold)">Chain</span>
          <span class="grid size-5 place-items-center rounded-full bg-gold font-display text-[0.7rem] font-extrabold tabular-nums text-void">{duel.chainDepth}</span>
        </div>
      </div>
    {/if}

    <!-- ══ "opponent is thinking", shown while the duel is blocked awaiting the opponent (their turn,
         or a response/chain window during yours), so a silent wait isn't a mystery. Centred on the
         field (past the info rail). ══ -->
    {#if oppThinking}
      <div class="pointer-events-none fixed top-[7%] right-0 z-[56] grid place-items-center" style="left:var(--info-w)" in:fade={{ duration: 150 }} out:fade={{ duration: 200 }}>
        <div class="flex items-center gap-2.5 chamfer bg-void/85 px-4 py-2 backdrop-blur" style="--c:7px;box-shadow:0 0 0 1px color-mix(in oklab,var(--color-magenta) 45%,transparent),0 0 20px color-mix(in oklab,var(--color-magenta) 22%,transparent)">
          <span class="flex items-end gap-1">
            <span class="think-dot"></span><span class="think-dot"></span><span class="think-dot"></span>
          </span>
          <span class="font-display text-[0.72rem] font-bold uppercase tracking-[0.18em] text-magenta" style="text-shadow:0 0 10px var(--color-magenta)">{duel.oppName} is thinking</span>
        </div>
      </div>
    {/if}

    <!-- ══ effect-activation banner, top-right under the LP cluster: WHO activates, the card, and
         WHICH effect (server-resolved text; the card's own text as fallback). The card itself
         levitates on the board while its chain link is live — this banner is the caption, the board
         is the show. Replaced the centre-screen card blow-up, which covered the field and read as
         "some random card image". ══ -->
    {#if duel.chainPop}
      {@const cp = duel.chainPop}
      {@const acc = cp.mine ? "var(--color-cyan)" : "var(--color-magenta)"}
      {@const effectText = cp.desc ?? info(cp.code)?.desc ?? ""}
      {#key cp.id}
        <div
          class="pointer-events-none fixed right-3 z-[57] w-[clamp(220px,20vw,300px)]"
          style="top:calc(8rem * var(--ui) * var(--lp-mult, 1.15))"
          in:fly={{ x: 26, duration: 240, easing: quintOut }}
          out:fade={{ duration: 220 }}
        >
          <div class="flex gap-2.5 chamfer bg-void/88 p-2.5 backdrop-blur" style="--c:7px;box-shadow:0 0 0 1px color-mix(in oklab,{acc} 50%,transparent),0 0 22px color-mix(in oklab,{acc} 20%,transparent),0 10px 30px rgba(0,0,0,.5)">
            <img src={cardImageLarge(cp.code)} alt={cp.name} class="h-[4.6rem] shrink-0 self-start chamfer object-cover aspect-[59/86]" style="--c:3px;box-shadow:0 0 0 1px color-mix(in oklab,{acc} 45%,transparent)" draggable="false" />
            <div class="min-w-0 flex-1">
              <div class="font-display text-[0.56rem] font-bold uppercase tracking-[0.22em]" style="color:{acc};text-shadow:0 0 10px {acc}">
                {cp.mine ? "You activate" : `${duel.oppName} activates`}{cp.link > 1 ? ` · Chain ${cp.link}` : ""}
              </div>
              <div class="truncate font-display text-[0.78rem] font-bold uppercase tracking-[0.04em] text-ink">{cp.name}</div>
              {#if effectText}
                <p class="mt-0.5 line-clamp-4 text-[0.62rem] leading-snug text-ink-dim">{effectText}</p>
              {/if}
            </div>
          </div>
        </div>
      {/key}
    {/if}

    <!-- corner buttons (true viewport corners) -->
    {#if !duel.ended}
      {#if duel.replayMode}
        <!-- replay control cluster (replaces Surrender — there is nothing to concede in a recording) -->
        <div class="fixed left-4 top-3 z-30 flex flex-col gap-2 chamfer bg-void/80 px-3.5 py-2.5 ring-1 ring-gold/25 backdrop-blur-sm" style="--c:8px">
          <div class="flex items-center gap-3 font-display text-xs uppercase tracking-[0.14em]">
            <span class="text-[0.6rem] font-bold tracking-[0.26em] text-gold" style="text-shadow:0 0 10px currentColor">Replay</span>
            <button class="grid size-7 place-items-center chamfer-btn bg-void/60 ring-1 ring-cyan/40 text-cyan transition-colors hover:bg-cyan/15 touch:size-9" style="--c:4px" title={duel.replayPaused ? "Play (resume)" : "Pause"} onclick={toggleReplayPause}>
              {#if duel.replayPaused}
                <svg viewBox="0 0 24 24" class="size-3.5" fill="currentColor" aria-hidden="true"><path d="M7 4.5v15l13-7.5z" /></svg>
              {:else}
                <svg viewBox="0 0 24 24" class="size-3.5" fill="currentColor" aria-hidden="true"><rect x="6" y="4.5" width="4" height="15" /><rect x="14" y="4.5" width="4" height="15" /></svg>
              {/if}
            </button>
            <button class="grid size-7 place-items-center chamfer-btn bg-void/60 ring-1 ring-cyan/40 text-cyan transition-colors hover:bg-cyan/15 touch:size-9" style="--c:4px" title="Next step (advances one action, pauses playback)" onclick={stepReplay}>
              <svg viewBox="0 0 24 24" class="size-3.5" fill="currentColor" aria-hidden="true"><path d="M5 4.5v15l10-7.5z" /><rect x="17" y="4.5" width="2.5" height="15" /></svg>
            </button>
            <div class="flex items-center gap-1">
              {#each [0.5, 1, 2, 4] as s (s)}
                <button class="chamfer-btn px-1.5 py-1 text-[0.68rem] font-bold tabular-nums transition-colors touch:px-2.5 {duel.replaySpeed === s ? 'bg-cyan/20 text-cyan' : 'text-ink-dim/60 hover:text-cyan'}" style="--c:3px" title="Playback speed {s}×" onclick={() => setReplaySpeed(s)}>{s}×</button>
              {/each}
            </div>
            <button class="text-ink-dim/80 transition-colors hover:text-magenta touch:-m-2 touch:p-2" onclick={() => go(duel.replayReturn)}>Exit</button>
          </div>
          <!-- progress track: items applied over the whole tape -->
          <div class="h-[3px] w-full overflow-hidden rounded-full bg-ink-dim/20">
            <div class="h-full rounded-full bg-gold/80 transition-[width] duration-200" style="width:{duel.replayLen ? (duel.replayPos / duel.replayLen) * 100 : 0}%"></div>
          </div>
        </div>
      {/if}
    {/if}

    <!-- ══ per-card action menu, always shows the available actions for the clicked card ══ -->
    {#if menu}
      <!-- opened from the pile viewer (Pro Mode GY/Extra actions) → ride ABOVE its z-58/59 layers -->
      <button class="fixed inset-0 {duel.pileView ? 'z-[60]' : 'z-40'} cursor-default" aria-label="Close menu" onclick={() => { menu = null; selectedCard = null; }}></button>
      <div class="fixed {duel.pileView ? 'z-[61]' : 'z-50'} flex w-52 flex-col chamfer bg-void/96 ring-1 ring-cyan/45 backdrop-blur" style="--c:8px;left:{menu.x}px;top:{menu.y}px;box-shadow:0 12px 44px rgba(0,0,0,.6)" in:scale={{ duration: 130, start: 0.92 }}>
        <div class="flex flex-col gap-1 p-1.5">
          {#each menu.actions as a (a.label + (a.choice.kind === "idle" || a.choice.kind === "battle" ? ":" + a.choice.cmd + ":" + (a.choice.index ?? 0) : ""))}
            <button class="chamfer-btn flex flex-col items-start border bg-void/60 px-3 py-2 touch:py-3 text-left font-display transition-colors {ACCENT[a.accent ?? 'cyan']}" style="--c:4px" onclick={() => runAction(a)}>
              <span class="text-xs font-bold uppercase tracking-wide">{a.label}</span>
              {#if a.sub}<span class="mt-0.5 line-clamp-2 font-body text-[0.6rem] normal-case tracking-normal text-ink-dim/70">{a.sub}</span>{/if}
            </button>
          {/each}
        </div>
      </div>
    {/if}

    <!-- ══ chain / card / tribute / option prompts (bottom overlay) ══ -->
    {#if prompt && (prompt.kind === "selectChain" || prompt.kind === "selectCard" || prompt.kind === "selectTribute" || prompt.kind === "selectUnselectCard" || prompt.kind === "selectOption")}
      <!-- Pro Mode: an OPTIONAL chain window must not reveal which cards can chain → hand DuelPrompts
           the neutral candidate list (your sets + hand) instead; the store validates each pick. A
           FORCED chain still shows the real list (it must be answered — that's not a hint). -->
      <DuelPrompts
        {prompt}
        {nameOf}
        prochain={duel.proMode && prompt.kind === "selectChain" && !prompt.forced ? proChainCandidates() : null}
        onprochain={proChainPick}
        onchoice={(c) => sendChoice(c)}
        onhover={(c) => { if (c.code) hovered = { ...c, position: c.position ?? 0 }; }}
      />
    {/if}

    <!-- ══ declare / order prompts ══ -->
    {#if prompt && declarePrompt}
      <DuelDeclare {prompt} {nameOf} onchoice={(c) => sendChoice(c)} />
    {/if}

    <!-- ══ on-board target pick, ALWAYS a prominent banner (incl 1-of attack/effect targets) ══ -->
    {#if duel.cardPick}
      {@const cp = duel.cardPick}
      {@const enough = pickedWeight(cp) >= cp.min}
      {@const isAttack = !!duel.aimFrom}
      <div class="pointer-events-none fixed inset-x-0 z-[55] flex justify-center px-3" style="bottom:calc(1.2rem * var(--ui))">
        <div class="pointer-events-auto flex items-center gap-4 chamfer bg-void/95 backdrop-blur-md target-pick" style="--c:12px;padding:calc(0.75rem*var(--ui)) calc(1.5rem*var(--ui));--pa:{isAttack ? 'var(--color-gold)' : 'var(--color-magenta)'}" in:fly={{ y: 22, duration: 180 }} out:fade={{ duration: 120 }}>
          <span class="reticle" style="width:calc(1.7rem*var(--ui));height:calc(1.7rem*var(--ui));color:var(--pa)"></span>
          <div class="flex flex-col leading-tight">
            <span class="font-display font-bold uppercase tracking-[0.14em]" style="font-size:calc(1rem*var(--ui));color:var(--pa);text-shadow:0 0 14px var(--pa)">{isAttack ? "Choose an attack target" : (cp.max > 1 ? `Choose targets ${pickLoc}` : `Choose a target ${pickLoc}`)}</span>
            <span class="font-body text-ink-dim/80" style="font-size:calc(0.74rem*var(--ui))">{cp.max > 1 ? `${pickedWeight(cp)} / ${cp.min === cp.max ? cp.min : `${cp.min}–${cp.max}`} selected` : `Click a glowing card ${pickLoc}, then Confirm`}</span>
          </div>
          <!-- Confirm is ALWAYS the sending step (even for a 1-of pick): clicking a candidate only
               selects + inspects it, so checking a face-down target can never resolve the effect. -->
          <button class="chamfer-btn border font-display font-bold uppercase tracking-wider {enough ? 'border-lime/70 text-lime hover:bg-lime/15' : 'border-ink-dim/25 text-ink-dim/40'}" style="--c:5px;padding:calc(0.5rem*var(--ui)) calc(1.3rem*var(--ui));font-size:calc(0.78rem*var(--ui))" disabled={!enough} onclick={confirmCardPick}>Confirm</button>
          {#if cp.cancelable}
            <button class="chamfer-btn border border-magenta/55 text-magenta hover:bg-magenta/15 font-display font-bold uppercase tracking-wider" style="--c:5px;padding:calc(0.5rem*var(--ui)) calc(1.1rem*var(--ui));font-size:calc(0.78rem*var(--ui))" onclick={cancelCardPick}>Cancel</button>
          {/if}
        </div>
      </div>
    {/if}

    <!-- ══ incremental cost pick (tribute / discard), click candidates one at a time ══ -->
    {#if duel.unselectPick}
      {@const up = duel.unselectPick}
      <div class="pointer-events-none fixed inset-x-0 z-[55] flex justify-center px-3" style="bottom:calc(1.2rem * var(--ui))">
        <div class="pointer-events-auto flex items-center gap-4 chamfer bg-void/95 backdrop-blur-md target-pick" style="--c:12px;padding:calc(0.75rem*var(--ui)) calc(1.5rem*var(--ui));--pa:var(--color-gold)" in:fly={{ y: 22, duration: 180 }} out:fade={{ duration: 120 }}>
          <span class="reticle" style="width:calc(1.7rem*var(--ui));height:calc(1.7rem*var(--ui));color:var(--pa)"></span>
          <div class="flex flex-col leading-tight">
            <span class="font-display font-bold uppercase tracking-[0.14em]" style="font-size:calc(1rem*var(--ui));color:var(--pa);text-shadow:0 0 14px var(--pa)">Choose which card{up.max > 1 ? "s" : ""} to pay</span>
            <span class="font-body text-ink-dim/80" style="font-size:calc(0.74rem*var(--ui))">{up.picked.length} / {up.min === up.max ? up.min : `${up.min}–${up.max}`} selected, click a glowing card</span>
          </div>
          <button class="chamfer-btn border font-display font-bold uppercase tracking-wider {up.finishable ? 'border-lime/70 text-lime hover:bg-lime/15' : 'border-ink-dim/25 text-ink-dim/40'}" style="--c:5px;padding:calc(0.5rem*var(--ui)) calc(1.3rem*var(--ui));font-size:calc(0.78rem*var(--ui))" disabled={!up.finishable} onclick={confirmUnselect}>Confirm</button>
          {#if up.cancelable}
            <button class="chamfer-btn border border-magenta/55 text-magenta hover:bg-magenta/15 font-display font-bold uppercase tracking-wider" style="--c:5px;padding:calc(0.5rem*var(--ui)) calc(1.1rem*var(--ui));font-size:calc(0.78rem*var(--ui))" onclick={confirmUnselect}>Cancel</button>
          {/if}
        </div>
      </div>
    {/if}

    <!-- ══ coin/dice toss + go-first/second prompt ══ -->
    <CoinFlip />

    <!-- ══ private opponent chat: docked in the card-info rail above; on narrow screens (no rail) it
         falls back to a compact bottom-left panel ══ -->
    {#if INFO_W === 0}
      <DuelChat />
    {/if}

    <!-- ══ modal decision (position / yes-no) + effect picker (multi-effect Activate) ══ -->
    {#if effectPick ?? modal}
      {@const d = (effectPick ?? modal)!}
      <div class="modal-scrim fixed inset-0 z-50 grid place-items-center bg-void/45" in:fade={{ duration: 150 }}>
        <!-- the effect picker is a client-side step (core still at idle) → backdrop cancels it; the
             forced position / yes-no modal has no backdrop-out (the core IS waiting on that answer). -->
        {#if effectPick}
          <button class="absolute inset-0 cursor-default" aria-label="Cancel" onclick={() => (effectPick = null)}></button>
        {/if}
        <div use:draggableDialog role="dialog" aria-modal="true" aria-label={d.title} class="relative flex flex-col items-center gap-4 chamfer bg-surface/95 p-6 ring-1 ring-cyan/40" style="--c:12px" in:scale={{ duration: 180, start: 0.94 }}>
          <div class="dialog-drag-handle -mx-2 -mt-3 h-5 shrink-0" data-dialog-drag-handle aria-hidden="true"></div>
          {#if d.code}<img src={cardImageLarge(d.code)} alt="card" class="w-28 chamfer" style="--c:6px" draggable="false" />{/if}
          <div class="max-w-xs text-center font-display text-sm font-bold uppercase tracking-wide text-ink">{d.title}</div>
          <!-- effect picker can have several long-labelled options → stack; the 2-option modal stays inline -->
          <div class="flex {effectPick ? 'w-72 flex-col' : 'gap-3'}">
            {#each d.options as o (o.label)}
              <button class="chamfer-btn border bg-void/70 px-5 py-2.5 text-center font-display text-sm font-bold tracking-wider transition-colors {effectPick ? 'normal-case' : 'uppercase'} {ACCENT[o.accent ?? 'cyan']}" style="--c:6px" onclick={() => { sendChoice(o.choice); effectPick = null; }}>{o.label}</button>
            {/each}
          </div>
        </div>
      </div>
    {/if}

    <!-- ══ pile viewer (graveyard / banished are public; extra/deck show a count) ══ -->
    {#if duel.pileView}
      <!-- in: only, NO outro — a full-viewport click-catcher must unmount synchronously (see the
           duel-options backdrop note above for the orphaned-outro click-shield failure). -->
      <button class="modal-scrim fixed inset-0 z-[58] cursor-default bg-void/42" aria-label="Close pile" onclick={closePile} in:fade={{ duration: 140 }}></button>
      <div use:draggableDialog role="dialog" aria-modal="true" aria-label={duel.pileView.title} class="fixed left-1/2 top-1/2 z-[59] flex max-h-[80dvh] w-[min(92vw,720px)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden chamfer bg-surface/96 ring-1 ring-cyan/40" style="--c:12px;box-shadow:0 20px 70px rgba(0,0,0,.6)" in:scale={{ duration: 200, start: 0.94 }}>
        <div data-dialog-drag-handle class="dialog-drag-handle flex items-center justify-between gap-3 border-b border-cyan/15 px-4 py-2.5">
          <span class="font-display text-sm font-bold uppercase tracking-[0.14em] text-cyan" style="text-shadow:0 0 12px var(--color-cyan)">{duel.pileView.title}</span>
          <span class="ml-auto font-display text-[0.62rem] uppercase tracking-wide text-ink-dim/60">{duel.pileView.cards.length + (duel.pileView.hiddenCount ?? 0)} cards</span>
          <button class="chamfer-btn border border-ink-dim/40 px-3 py-1 font-display text-[0.58rem] font-bold uppercase tracking-wider text-ink-dim transition-colors hover:text-ink" style="--c:3px" onclick={closePile}>Close</button>
        </div>
        <div class="overflow-y-auto scroll-cyber p-4">
          {#if duel.pileView.cards.length === 0 && !duel.pileView.hiddenCount}
            <p class="py-10 text-center font-display text-sm uppercase tracking-[0.18em] text-ink-dim/75">Empty</p>
          {:else}
            {#if duel.pileView.cards.length > 0}
              <div class="flex flex-wrap justify-center gap-2.5">
                {#each duel.pileView.cards as card, i (i)}
                  <!-- hover a card → the left info rail shows its art + text (rail is raised above this
                       viewer while it's open). Pro Mode + your idle/battle window: clicking opens the
                       type menu (GY/banished → Activate, Extra → Special Summon); the validation
                       inside proPlaysFor rejects opponent piles / illegal picks. -->
                  <button type="button" aria-label={nameOf(card.code)} class="w-[clamp(64px,9vw,92px)] overflow-hidden chamfer ring-1 ring-cyan/30 transition-transform hover:scale-105 hover:ring-cyan/70" style="--c:6px" title={nameOf(card.code)} onmouseenter={() => setHover(card, true)} onmouseleave={() => setHover(card, false)} onclick={(e) => clickCard(card, e)}>
                    <img src={cardImageLarge(card.code)} alt={nameOf(card.code)} class="aspect-[59/86] w-full object-cover" draggable="false" />
                  </button>
                {/each}
              </div>
            {/if}
            {#if duel.pileView.hiddenCount}
              <!-- face-down Extra-Deck monsters (Fusion/Synchro/Xyz/Link) you can't see (opponent's, or hidden) -->
              <p class="{duel.pileView.cards.length > 0 ? 'mt-4 border-t border-ink-dim/15 pt-3' : 'py-10'} text-center font-display text-xs uppercase tracking-[0.18em] text-ink-dim/75">+ {duel.pileView.hiddenCount} face-down card{duel.pileView.hiddenCount === 1 ? "" : "s"}</p>
            {/if}
          {/if}
        </div>
      </div>
    {/if}

    <!-- ══ surrender confirm (raised by clicking your own deck) ══ -->
    {#if duel.askSurrender}
      <div class="modal-scrim fixed inset-0 z-[60] grid place-items-center bg-void/48" in:fade={{ duration: 150 }}>
        <div use:draggableDialog role="alertdialog" aria-modal="true" aria-label="Surrender the duel" class="flex flex-col items-center gap-3.5 chamfer bg-surface/95 p-6 ring-1 ring-magenta/40" style="--c:12px" in:scale={{ duration: 180, start: 0.94 }}>
          <div class="dialog-drag-handle -mx-2 -mt-3 h-5 shrink-0" data-dialog-drag-handle aria-hidden="true"></div>
          <div class="text-center font-display text-sm font-bold uppercase tracking-wide text-ink">Surrender the duel?</div>
          <p class="max-w-xs text-center font-display text-[0.62rem] uppercase tracking-wide text-ink-dim/65">Your opponent wins. This can't be undone.</p>
          <div class="flex gap-3">
            <button class="chamfer-btn border border-magenta/60 bg-void/70 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-magenta transition-colors hover:bg-magenta/15" style="--c:6px" onclick={confirmSurrender}>Surrender</button>
            <button class="chamfer-btn border border-cyan/60 bg-void/70 px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-cyan transition-colors hover:bg-cyan/15" style="--c:6px" onclick={dismissSurrender}>Keep Playing</button>
          </div>
        </div>
      </div>
    {/if}

    <!-- ══ end overlay ══ -->
    <!-- Bo3: per-game end is silent (the SideOverlay takes over); only the MatchEnd shows the big
         victory/defeat card. Bo1 keeps its existing end overlay. -->
    {#if duel.ended && duel.bestOf === 1}
      <div class="modal-scrim fixed inset-0 z-[60] grid place-items-center bg-void/48" in:fade>
        <div class="flex flex-col items-center gap-6" in:scale={{ duration: 350, start: 0.85, delay: 100 }}>
          <div class="font-display text-6xl font-bold uppercase tracking-[0.1em] {duel.youWon ? 'text-lime' : 'text-magenta'}" style="text-shadow:0 0 40px currentColor">{duel.youWon ? "Victory" : "Defeat"}</div>
          {#if duel.dpAwarded > 0}
            <div class="chamfer-btn bg-gold/15 px-5 py-2 font-display text-lg font-bold tracking-wide text-gold glow-gold" style="--c:7px" in:scale={{ duration: 300, start: 0.7, delay: 350 }}>+ {duel.dpAwarded} DP</div>
          {/if}
          {#if duel.rankResult}
            {@const rr = duel.rankResult}
            <div class="flex flex-col items-center gap-2 chamfer-btn bg-void/70 px-6 py-3 ring-1 ring-cyan/30" style="--c:8px" in:scale={{ duration: 300, start: 0.7, delay: 450 }}>
              <div class="flex items-center gap-3">
                <span class="font-display text-[0.62rem] uppercase tracking-[0.2em] text-ink-dim">Rating</span>
                <span class="font-display text-2xl font-bold tabular-nums {rr.delta >= 0 ? 'text-lime' : 'text-magenta'}" style="text-shadow:0 0 14px currentColor">{rr.delta >= 0 ? "+" : ""}{rr.delta}</span>
                <RankBadge rank={rr.info} />
              </div>
              {#if rr.promoted}<div class="font-display text-xs font-semibold uppercase tracking-[0.18em] text-gold">★ Promoted, {rr.info.label}</div>{/if}
              {#if rr.unlockedUniform}<div class="font-body text-xs text-cyan">{uniformName(rr.unlockedUniform)} uniform unlocked, equip it in Customize Avatar</div>{/if}
            </div>
          {/if}
          <div class="flex items-center gap-3">
            {#if !duel.replayMode}
              <button class="chamfer-btn border border-gold/50 bg-void/70 px-6 py-3 font-display text-sm font-bold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold/15" style="--c:8px" onclick={saveReplay}>Save Replay</button>
            {/if}
            <button class="chamfer-btn border border-cyan/60 bg-void/70 px-8 py-3 font-display text-sm font-bold uppercase tracking-[0.2em] text-cyan transition-colors hover:bg-cyan/15" style="--c:8px" onclick={() => go(duel.replayMode ? duel.replayReturn : afterDuelRoute())}>{duel.replayMode ? "Back" : session.mode === "free" ? "Return to Lobby" : "Return to World"}</button>
          </div>
        </div>
      </div>
    {/if}
  {/if}

  <!-- ══ Bo3 side-deck overlay (between games) ══ -->
  {#if duel.sidePhase}
    <SideOverlay
      gameNo={duel.sidePhase.gameNo}
      score={duel.sidePhase.score}
      deadlineMs={duel.sidePhase.deadlineMs}
      deck={duel.sidePhase.deck}
      pickTurn={duel.sidePhase.pickTurn}
    />
  {/if}

  <!-- ══ Bo3 match end card (replaces the Bo1 end overlay) ══ -->
  {#if duel.matchEnd}
    {@const me2 = duel.matchEnd}
    <div class="modal-scrim fixed inset-0 z-[60] grid place-items-center bg-void/48" in:fade>
      <div class="flex flex-col items-center gap-6" in:scale={{ duration: 350, start: 0.85, delay: 100 }}>
        <div class="font-display text-[0.66rem] font-bold uppercase tracking-[0.32em] text-ink-dim">Match</div>
        <div class="font-display text-6xl font-bold uppercase tracking-[0.1em] {me2.youWon ? 'text-lime' : 'text-magenta'}" style="text-shadow:0 0 40px currentColor">{me2.youWon ? "Match Won" : "Match Lost"}</div>
        <div class="flex items-baseline gap-3 chamfer-btn bg-void/70 px-5 py-2 ring-1 ring-cyan/30 font-display text-2xl font-bold tabular-nums" style="--c:7px">
          <span class="text-cyan">{me2.finalScore.you}</span>
          <span class="text-base text-ink-dim">vs</span>
          <span class="text-magenta">{me2.finalScore.opp}</span>
        </div>
        {#if me2.rankResult}
          {@const rr = me2.rankResult}
          <div class="flex flex-col items-center gap-2 chamfer-btn bg-void/70 px-6 py-3 ring-1 ring-cyan/30" style="--c:8px">
            <div class="flex items-center gap-3">
              <span class="font-display text-[0.62rem] uppercase tracking-[0.2em] text-ink-dim">Rating</span>
              <span class="font-display text-2xl font-bold tabular-nums {rr.delta >= 0 ? 'text-lime' : 'text-magenta'}" style="text-shadow:0 0 14px currentColor">{rr.delta >= 0 ? "+" : ""}{rr.delta}</span>
              <RankBadge rank={rr.info} />
            </div>
            {#if rr.promoted}<div class="font-display text-xs font-semibold uppercase tracking-[0.18em] text-gold">★ Promoted, {rr.info.label}</div>{/if}
          </div>
        {/if}
        <div class="flex items-center gap-3">
          <button class="chamfer-btn border border-gold/50 bg-void/70 px-6 py-3 font-display text-sm font-bold uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold/15" style="--c:8px" onclick={saveReplay}>Save Replay</button>
          <button class="chamfer-btn border border-cyan/60 bg-void/70 px-8 py-3 font-display text-sm font-bold uppercase tracking-[0.2em] text-cyan transition-colors hover:bg-cyan/15" style="--c:8px" onclick={() => go(afterDuelRoute())}>{session.mode === "free" ? "Return to Lobby" : "Return to World"}</button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  /* Dismissible card details are a compact inspector, not a second full-height sidebar. The card art
     gets a fixed readable cap; remaining text scrolls inside the panel rather than making the board
     feel occluded. These rules deliberately scope to the duel overlay, not the reusable rail/editor. */
  /* The panel is height:auto capped by max-height, so a short vanilla card gets a short panel instead
     of a fixed-tall one (owner: "not bigger than necessary"), while a long card caps and its
     description SCROLLS. That needs an unbroken flex column from the panel down to the scroll region:
     the body is a plain block in the shared component (it breaks the chain), so re-flex it here and
     replace `content`'s percentage `h-full` with flex sizing. Every level carries min-height:0 so the
     description is the piece that shrinks + scrolls when the cap is hit. */
  :global(.df-carddetail .tp-cardinfo-body) { padding: 0.65rem 0.75rem 0.7rem; display: flex; flex-direction: column; min-height: 0; }
  :global(.df-carddetail .tp-cardinfo-content) { gap: 0.45rem; height: auto; flex: 1 1 auto; min-height: 0; }
  :global(.df-carddetail .tp-cardinfo-desc) { flex: 1 1 auto; min-height: 0; }
  :global(.df-carddetail .tp-cardinfo-art) {
    align-self: center;
    display: flex;
    width: auto;
    height: clamp(7.5rem, 18vh, 10.25rem);
    max-width: 100%;
    max-height: none;
    justify-content: center;
  }
  :global(.df-carddetail .tp-cardinfo-art img) { width: auto; height: 100%; aspect-ratio: 59 / 86; object-fit: contain; }
  :global(.df-carddetail .tp-cardinfo-name) {
    display: -webkit-box;
    overflow: hidden;
    font-size: 0.92rem;
    line-height: 1.1;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }
  :global(.df-carddetail .tp-cardinfo-level) { gap: 0.35rem; }
  :global(.df-carddetail .tp-cardinfo-type) { font-size: 0.62rem; }
  :global(.df-carddetail .tp-cardinfo-stats) { font-size: 0.8rem; gap: 0.7rem; }
  :global(.df-carddetail .tp-cardinfo-desc) { font-size: 0.67rem; line-height: 1.45; }
  :global(.phone .df-carddetail .tp-cardinfo-body) { padding: 0.5rem 0.6rem 0.55rem; }
  /* Phone compact layout (see CardInfoRail): the art is a HEADER THUMBNAIL beside the name column,
     not a hero image — the description below owns the remaining panel height and scrolls. */
  :global(.phone .df-carddetail .tp-cardinfo-content) { gap: 0.4rem; }
  :global(.phone .df-carddetail .tp-cardinfo-art) { align-self: flex-start; height: 5.4rem; }
  :global(.phone .df-carddetail .tp-cardinfo-name) { font-size: 0.8rem; }
  :global(.phone .df-carddetail .tp-cardinfo-desc) { font-size: 0.64rem; line-height: 1.4; }

  /* ── Field geometry ────────────────────────────────────────────────────────────
     There is deliberately NO default playmat surface here. The app's live WebGPU CyberBackground is
     already the arena; this plane exists only to hold legal YGO zone geometry in perspective. A texture
     pack may still opt in with --df-field, but an unthemed duel stays transparent. */
  .duel-plane {
    width: min(1040px, calc(100% - 10px));
    height: calc(var(--zone-track) * 5.15);
    transform-style: preserve-3d;
  }
  .tp-field {
    z-index: 0;
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
  }
  /* The field sits DEAD-CENTRE horizontally — the card inspector is a dismissible overlay now, so
     no register shift compensates for it. Only a small vertical lift remains (hand-fan clearance). */
  .tp-mat {
    --field-register-y: clamp(-34px, -2.2vh, -18px);
    translate: 0 var(--field-register-y);
    transform-style: preserve-3d;
    /* Large transformed layout boxes must never win the browser's projected hit test. Interactive
       descendants opt back in below, so a visible card remains clickable at every camera pitch. */
    pointer-events: none;
  }
  .field-block {
    position: relative;
    z-index: 5;
    width: var(--main-grid-w);
    height: calc(var(--zone-track) * 2 + var(--zone-gap));
    flex: none;
    transform-style: preserve-3d;
  }
  .field-core {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    transform-style: preserve-3d;
  }
  /* Independent side slots use the official duel-field map. They intentionally sit outside flow so
     neither a deck stack nor an empty GY can ever alter the shared five-column centre axis. */
  .field-perimeter {
    --perimeter-gutter: clamp(8px, 1vw, 16px);
    position: absolute;
    z-index: 8;
    display: grid;
    width: var(--zone-side);
    height: var(--zone-track);
    place-items: center;
    transform-style: preserve-3d;
  }
  /* Official player map, viewed from your seat:
       Field / Extra  ← left of the five-column bank
       Graveyard / Deck → right of the bank
     The graveyard and deck are deliberately not forced onto a main-row baseline. Banish is not an
     official Zone, so it is a companion pile stacked directly above the Graveyard. */
  .field-block-mine .field-perimeter-field,
  .field-block-mine .field-perimeter-extra { right: calc(100% + var(--perimeter-gutter)); }
  .field-block-mine .field-perimeter-gy,
  .field-block-mine .field-perimeter-deck,
  .field-block-mine .field-perimeter-banish { left: calc(100% + var(--perimeter-gutter)); }
  .field-block-mine .field-perimeter-field { top: calc(var(--zone-track) * 0.18); }
  .field-block-mine .field-perimeter-gy { top: calc(var(--zone-track) * 0.28); }
  .field-block-mine .field-perimeter-extra { top: calc(var(--zone-track) * 1.1 + var(--zone-gap)); }
  .field-block-mine .field-perimeter-deck { top: calc(var(--zone-track) * 1.18 + var(--zone-gap)); }
  .field-block-mine .field-perimeter-banish {
    top: calc(var(--zone-track) * -0.82);
  }
  /* Opponent: the exact 180° rotation of the official player map. */
  .field-block:not(.field-block-mine) .field-perimeter-field,
  .field-block:not(.field-block-mine) .field-perimeter-extra { left: calc(100% + var(--perimeter-gutter)); }
  .field-block:not(.field-block-mine) .field-perimeter-gy,
  .field-block:not(.field-block-mine) .field-perimeter-deck,
  .field-block:not(.field-block-mine) .field-perimeter-banish { right: calc(100% + var(--perimeter-gutter)); }
  .field-block:not(.field-block-mine) .field-perimeter-field { top: calc(var(--zone-track) * 0.82 + var(--zone-gap)); }
  .field-block:not(.field-block-mine) .field-perimeter-gy { top: calc(var(--zone-track) * 0.72 + var(--zone-gap)); }
  .field-block:not(.field-block-mine) .field-perimeter-extra { top: calc(var(--zone-track) * -0.1); }
  .field-block:not(.field-block-mine) .field-perimeter-deck { top: calc(var(--zone-track) * -0.18); }
  .field-block:not(.field-block-mine) .field-perimeter-banish {
    top: calc(var(--zone-track) * 1.82 + var(--zone-gap));
  }
  .field-bridge {
    position: relative;
    z-index: 6;
    min-height: calc(var(--zone-track) * 0.1);
    flex: none;
    transform-style: preserve-3d;
  }
  .field-bridge-link { min-height: calc(var(--zone-track) * 0.94); }

  /* The compact traditional field has no shared Extra Monster Zone row separating the two halves.
     Its five-zone cores stay close together, while each complete perimeter rail moves outward. Moving
     only half of each rail left the mirrored pair (your Field Zone / opponent Banish) overlapping.
     Free Duel carries .df-emz instead, so its already-correct Link layout remains untouched. */
  .df-no-emz {
    --non-link-perimeter-clearance: calc(var(--zone-track) * 0.27 + var(--zone-gap) * 0.5);
  }
  .df-no-emz .field-block:not(.field-block-mine) .field-perimeter {
    translate: 0 calc(var(--non-link-perimeter-clearance) * -1);
  }
  .df-no-emz .field-block-mine .field-perimeter {
    translate: 0 var(--non-link-perimeter-clearance);
  }

  /* Explicit perspective-safe interaction surfaces. Empty layout cells and their card-sized motion
     anchors cannot intercept a card that visually projects over them. */
  .field-card-face,
  .pile-zone,
  .place-target,
  .zone-hover-target {
    pointer-events: auto;
  }
  .field-card-anchor { pointer-events: none; }
  .mat3d .field-card-volume { transform: translateZ(7px); }

  /* Manual zone-pick highlight: a pulsing lime fill + ring on each legal zone (mirrors the 3D arena's
     lime placement glow), so picking a zone works the same in the 2D board. */
  .place-target {
    cursor: pointer;
    background: color-mix(in oklab, var(--color-lime) 16%, transparent);
    box-shadow: inset 0 0 0 2px color-mix(in oklab, var(--color-lime) 75%, transparent),
                0 0 16px color-mix(in oklab, var(--color-lime) 40%, transparent);
    animation: place-pulse 1.15s ease-in-out infinite;
  }
  .place-target:hover { background: color-mix(in oklab, var(--color-lime) 28%, transparent); }
  @keyframes place-pulse { 0%, 100% { opacity: 0.55; } 50% { opacity: 1; } }

  /* ── landscape-phone adjustments — see lib/device.svelte.ts for the forceable pattern ── */
  :global(.phone) .duel-stage { --card-scale: 1.12; }
  :global(.df-emz) { --card-scale: 0.9; }
  :global(.df-emz) .duel-plane { height: calc(var(--zone-track) * 6.15); }
  :global(.phone .df-emz) { --card-scale: 0.96; }
  :global(.phone) .duel-plane {
    width: calc(100% - 8px);
    height: calc(var(--zone-track) * 4.98);
  }
  :global(.phone) .tp-mat {
    --field-register-y: clamp(-20px, -3.6vh, -12px);
  }
  :global(.phone .df-emz) .duel-plane { height: calc(var(--zone-track) * 5.92); }
  :global(.phone .df-emz) .field-bridge-link { min-height: calc(var(--zone-track) * 0.84); }
  /* Busts stay fully OPAQUE (owner mandate — no ghost duelists) and ride slightly above the stage
     floor so more of the figure shows; phones lift further, clearing the bottom-right phase orb. */
  .duel-bust { margin-bottom: 16px; }
  :global(.phone) .duel-bust { margin-bottom: 34px; }
  /* ── phone hands: EDGE-TUCKED, the mobile-TCG grammar (Duel Links / MD mobile / Hearthstone) ──
     The field owns the middle band; each hand hugs its screen edge with roughly half of every card
     CROPPED off-screen (owner-approved). Your fan stays big (thumb targets) but slides down; the
     opponent's slides up behind their rows. The individual `translate` property composes with the
     row's inline transform (theme vars untouched). */
  :global(.phone) .tp-hand[data-mref-hand="me"] {
    --df-hand-size: 1.25;
    translate: 0 calc(var(--ch) * 0.78);
  }
  :global(.phone) .tp-hand[data-mref-hand="opp"] {
    translate: 0 calc(var(--ch) * -0.42);
  }
  /* The CLICKED card raises (selected / menu open) and drops back on dismiss — never on hover. */
  .hand-raised {
    translate: 0 -16px;
    scale: 1.05;
  }
  /* Phone: the clicked card rises fully OUT of the edge-tuck — full art in reach. */
  :global(.phone) .tp-hand[data-mref-hand="me"] .duel-hand-lift.hand-raised {
    translate: 0 calc(var(--ch) * -0.72);
  }
  /* Flat/top-down uses the same hand fan, but has no camera recession to clear the opponent's S/T row.
     Lift only that mode; the perspective presentation already has the correct clearance. (The phone
     tuck above already clears it there — its element-level translate wins over this class.) */
  .flat-opp-hand-clearance { translate: 0 clamp(-52px, -4.2vh, -30px); }
  /* LP chips read fine smaller on a short viewport — reclaims the top-right corner. */
  :global(.phone) .duel-sizer {
    --lp-mult: 0.88;
  }
  /* Ultrawide: even after the second --ui ramp the corner LP clusters deserve an extra kick on a
     2000px+ monitor (owner: "tiny"). The phase orb's top offset scales along so the (now taller)
     opponent LP chip can't overlap it — its fixed 4rem was only clear of chip-bottoms up to
     --ui 1.18. Phones never reach 1900px; the .phone rule above out-specifies this one anyway,
     and the orb rule is html:not(.phone)-guarded for the ?phone=1 forced-test case. */
  @media (min-width: 1900px) {
    .duel-sizer {
      --lp-mult: 1.12;
    }
    :global(html:not(.phone)) .df-phase {
      top: calc(4rem * var(--ui) * var(--lp-mult, 1));
    }
  }
  /* Auto toggles size off --ui inline; on touch they still land ~20px tall, so
     force real padding here (!important beats the inline style — one place, scoped). */
  :global(.touch .df-autotoggles button) {
    padding: 0.5rem 0.8rem !important;
  }
  :global(.phone) .df-options-trigger {
    right: calc(6.25rem * var(--ui));
  }
  :global(.phone) .df-options-menu {
    right: 0.5rem;
    bottom: calc(5.9rem * var(--ui));
    max-height: calc(100dvh - 6.8rem * var(--ui));
  }

  /* "Opponent is thinking", three magenta dots PULSING in sequence — a data heartbeat (glow + opacity),
     not a vertical bounce. Motion = data, never bounce like a toy. */
  .think-dot {
    width: 0.4rem; height: 0.4rem; border-radius: 9999px;
    background: var(--color-magenta);
    box-shadow: 0 0 5px var(--color-magenta);
    animation: think-pulse 1.1s ease-in-out infinite;
  }
  .think-dot:nth-child(2) { animation-delay: 0.18s; }
  .think-dot:nth-child(3) { animation-delay: 0.36s; }
  @keyframes think-pulse {
    0%, 80%, 100% { opacity: 0.4; box-shadow: 0 0 4px var(--color-magenta); }
    40% { opacity: 1; box-shadow: 0 0 9px var(--color-magenta), 0 0 16px color-mix(in oklab, var(--color-magenta) 60%, transparent); }
  }

  /* Chain levitation (2D): the activating card lifts off the mat while its chain link is live —
     individual translate/scale props compose with the slot's inline `transform`, so the playmat
     alignment vars keep working; everything transitions back the frame the link resolves. */
  [data-mref] {
    transition: translate 0.55s cubic-bezier(0.22, 1, 0.36, 1), scale 0.55s cubic-bezier(0.22, 1, 0.36, 1), filter 0.55s ease;
    transform-style: preserve-3d;
  }
  .chain-levit {
    translate: 0 -7px;
    scale: 1.06;
    filter: drop-shadow(0 12px 10px rgba(0, 0, 0, 0.55));
    z-index: 15;
  }
  /* The chain ring / target reticle must WIN THE DEPTH TEST, not just the z-index one: the board
     paints preserve-3d, where the card volume floats at translateZ(7px) and a full pile stacks to
     ~20px — an overlay left at Z=0 is genuinely BEHIND the card it marks (the "card hides the
     chain" bug). 26px clears the tallest stack; in flat view (no perspective) it's invisible.
     Svelte's scale transition preserves the computed transform, so intros/outros keep the depth. */
  .chain-overlay { transform: translateZ(26px); }

  /* The pile version: the activating card's art hovers above the stack, gently bobbing. Bob rides
     the `translate` property (keeping the -50% x centring in the keyframes) so `transform` stays
     free to carry the same preserve-3d depth lift as the badge. */
  .chain-hover-card {
    transform: translateZ(26px);
    animation: chain-bob 3.2s ease-in-out infinite;
    box-shadow: 0 0 0 1px color-mix(in oklab, var(--color-gold) 55%, transparent), 0 14px 18px rgba(0, 0, 0, 0.55);
  }
  @keyframes chain-bob {
    0%, 100% { translate: -50% 0; }
    50% { translate: -50% -5px; }
  }

  /* Classic chain marker: the golden link-ring slowly rotates over the activating card; the number
     stays upright on its centre. Static (but still shown) under reduced motion. */
  .chain-ring {
    animation: chain-spin 8s linear infinite;
    filter: drop-shadow(0 0 5px color-mix(in oklab, var(--color-gold) 65%, transparent));
  }
  @keyframes chain-spin { to { transform: rotate(360deg); } }
  .chain-num {
    font-size: calc(var(--cw) * 0.52);
    line-height: 1;
    color: #ffe9b0;
    -webkit-text-stroke: calc(var(--cw) * 0.045) #04060c;
    paint-order: stroke fill;
    text-shadow: 0 0 10px color-mix(in oklab, var(--color-gold) 70%, transparent), 0 0 3px rgba(0, 0, 0, 0.9);
  }
  @media (prefers-reduced-motion: reduce) {
    .chain-ring { animation: none; }
  }

  /* Flanking duelist busts: soft cast shadow + a HEM-ONLY fade at the waist cut (the old 76% fade
     ghosted the whole lower quarter — owner: avatars must read fully solid). */
  .duel-bust {
    filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.5));
    -webkit-mask-image: linear-gradient(to bottom, #000 93%, transparent);
    mask-image: linear-gradient(to bottom, #000 93%, transparent);
  }
  /* (Wide-desktop clearance is handled generally now: the inline margins fold in the letterbox width
     (edgePush, clamped to EDGE_PUSH_MAX) so both busts follow the viewport edge just far enough to
     clear the rail piles, then hug the board on ultrawide letterboxes.) */
  /* Phone: no letterbox exists (the stage fills the width), but the field also fills it — push the
     opponent's bust further into the right bezel so the FACE clears the rail piles; the outer
     shoulder cropping off-screen is the intended trade (owner: face visibility is the priority). */
  :global(.phone) .duel-bust-opp { translate: 44px 0; }

  /* prominent on-board target banner, accent ring + pulsing reticle so it reads as "point at the board". */
  .target-pick {
    box-shadow: 0 0 0 1.5px color-mix(in oklab, var(--pa) 65%, transparent),
                0 0 24px color-mix(in oklab, var(--pa) 30%, transparent),
                0 12px 44px rgba(0, 0, 0, 0.55);
  }
  .reticle {
    border: 2px solid currentColor; border-radius: 50%;
    box-shadow: 0 0 12px currentColor, inset 0 0 8px currentColor;
    position: relative; animation: reticle-pulse 1.3s ease-in-out infinite;
  }
  .reticle::before, .reticle::after { content: ""; position: absolute; background: currentColor; inset: 0; margin: auto; }
  .reticle::before { width: 60%; height: 2px; }
  .reticle::after { width: 2px; height: 60%; }
  @keyframes reticle-pulse { 0%, 100% { transform: scale(1); opacity: 0.9; } 50% { transform: scale(1.15); opacity: 0.55; } }

  /* BECOME_TARGET lock-on: a bold ring + outer glow over the targeted card. Double inset (dark backing
     under the accent) keeps it legible over any card art / texture-pack theme, not just on a dark mat. */
  .target-mark {
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.65),
                inset 0 0 0 3px color-mix(in oklab, var(--color-magenta) 90%, white 12%),
                0 0 22px color-mix(in oklab, var(--color-magenta) 60%, transparent);
    animation: target-flash 0.7s ease-in-out infinite;
  }
  @keyframes target-flash { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }

  /* ── Printed zone frames ── these deliberately have no dark base. The CyberBackground is the arena;
     a socket is only a transparent tinted frame, as in the former Canvas duel mat. The nested octagon
     and corner ticks retain the premium Master-Duel read without becoming a sci-fi control-panel tile. */
  .zone-socket,
  .pile-socket {
    --d: var(--df-zone-dashed, 0);
    overflow: hidden;
    background: linear-gradient(
      180deg,
      color-mix(in oklab, currentColor calc(11% * (1 - var(--d))), transparent),
      color-mix(in oklab, currentColor calc(3% * (1 - var(--d))), transparent)
    );
    box-shadow:
      0 2px 8px rgba(0, 0, 0, 0.14),
      inset 0 0 0 1.5px color-mix(in oklab, currentColor calc(92% * (1 - var(--d))), transparent),
      inset 0 0 14px color-mix(in oklab, currentColor calc(18% * (1 - var(--d))), transparent);
    border: 1px solid color-mix(in oklab, currentColor calc(38% * (1 - var(--d))), color-mix(in oklab, var(--color-ink) calc(52% * var(--d)), transparent));
  }
  .zone-socket::before,
  .pile-socket::before {
    content: "";
    position: absolute;
    inset: 11%;
    pointer-events: none;
    clip-path: polygon(24% 0, 76% 0, 100% 24%, 100% 76%, 76% 100%, 24% 100%, 0 76%, 0 24%);
    border: 1px solid color-mix(in oklab, currentColor calc(48% * (1 - var(--d))), transparent);
    box-shadow:
      inset 0 0 0 1px color-mix(in oklab, currentColor calc(5% * (1 - var(--d))), transparent),
      inset 0 0 10px color-mix(in oklab, currentColor calc(12% * (1 - var(--d))), transparent);
  }
  .zone-socket::after,
  .pile-socket::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    opacity: calc(0.86 * (1 - var(--d)));
    background:
      linear-gradient(currentColor, currentColor) 24% 0 / 14% 2px no-repeat,
      linear-gradient(currentColor, currentColor) 0 24% / 2px 14% no-repeat,
      linear-gradient(currentColor, currentColor) 62% 0 / 14% 2px no-repeat,
      linear-gradient(currentColor, currentColor) 100% 24% / 2px 14% no-repeat,
      linear-gradient(currentColor, currentColor) 24% 100% / 14% 2px no-repeat,
      linear-gradient(currentColor, currentColor) 0 62% / 2px 14% no-repeat,
      linear-gradient(currentColor, currentColor) 62% 100% / 14% 2px no-repeat,
      linear-gradient(currentColor, currentColor) 100% 62% / 2px 14% no-repeat;
  }
  .zone-socket > span { position: relative; z-index: 1; }

  /* Extra Monster Zones are shared territory, so their material is neutral silver rather than
     implying that either cyan or magenta player owns the socket. */
  .zone-socket-neutral {
    color: color-mix(in oklab, var(--color-ink) 62%, var(--color-void) 38%);
  }

  /* Empty regular M/S sockets answer hover with a visible, translucent energy pulse. It stays on the
     printed plate (never an opaque tile), and cards do not animate the empty-zone affordance below. */
  @media (hover: hover) and (pointer: fine) {
    [data-fe="mzone"] .field-slot-empty:hover .zone-socket,
    [data-fe="szone"] .field-slot-empty:hover .zone-socket,
    [data-fe="mzone"] .field-slot-empty:global(.zone-slot-hovered) .zone-socket,
    [data-fe="szone"] .field-slot-empty:global(.zone-slot-hovered) .zone-socket {
      background:
        radial-gradient(circle at 50% 48%, color-mix(in oklab, currentColor calc(21% * (1 - var(--d))), transparent), transparent 70%),
        linear-gradient(
        180deg,
        color-mix(in oklab, currentColor calc(27% * (1 - var(--d))), transparent),
        color-mix(in oklab, currentColor calc(9% * (1 - var(--d))), transparent));
      border-color: color-mix(in oklab, currentColor calc(90% * (1 - var(--d))), transparent);
      animation: zone-slot-hover 1.4s cubic-bezier(0.22, 1, 0.36, 1) infinite;
    }
  }
  @keyframes zone-slot-hover {
    0%, 100% {
      filter: brightness(1);
      box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.14),
        inset 0 0 0 1.5px color-mix(in oklab, currentColor calc(88% * (1 - var(--d))), transparent),
        inset 0 0 18px color-mix(in oklab, currentColor calc(30% * (1 - var(--d))), transparent),
        0 0 10px color-mix(in oklab, currentColor calc(22% * (1 - var(--d))), transparent);
    }
    50% {
      filter: brightness(1.22);
      box-shadow:
        0 2px 8px rgba(0, 0, 0, 0.14),
        inset 0 0 0 1.5px color-mix(in oklab, currentColor calc(100% * (1 - var(--d))), transparent),
        inset 0 0 28px color-mix(in oklab, currentColor calc(48% * (1 - var(--d))), transparent),
        0 0 30px color-mix(in oklab, currentColor calc(54% * (1 - var(--d))), transparent);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    [data-fe="mzone"] .field-slot-empty:hover .zone-socket,
    [data-fe="szone"] .field-slot-empty:hover .zone-socket,
    [data-fe="mzone"] .field-slot-empty:global(.zone-slot-hovered) .zone-socket,
    [data-fe="szone"] .field-slot-empty:global(.zone-slot-hovered) .zone-socket {
      animation: none;
      filter: brightness(1.12);
      box-shadow:
        inset 0 0 0 1.5px color-mix(in oklab, currentColor calc(100% * (1 - var(--d))), transparent),
        inset 0 0 22px color-mix(in oklab, currentColor calc(40% * (1 - var(--d))), transparent),
        0 0 20px color-mix(in oklab, currentColor calc(38% * (1 - var(--d))), transparent);
    }
  }
  .pile-caption { line-height: 1; text-shadow: 0 1px 2px rgba(0, 0, 0, 0.88); }
  .field-slot,
  [data-fe],
  .field-card-anchor,
  .field-card-volume,
  .pile-zone,
  .pile-stack { transform-style: preserve-3d; }
  /* Cards stay visually dominant, with only their natural contact shadow above the printed frame. */
  .field-card-shadow {
    border-radius: 48%;
    opacity: 0.15;
    background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.64), rgba(0, 0, 0, 0) 68%);
    transform: translateY(5px) scale(0.88, 0.38);
  }
  .field-card-face { z-index: 1; }
  .pile-stack {
    z-index: 2;
  }
  .pile-motion-anchor,
  .pile-card-layer,
  .pile-follows-top {
    transform: translate3d(var(--flat-x, 0px), var(--flat-y, 0px), 0);
    transform-origin: 50% 50%;
  }
  .pile-ground-shadow {
    border-radius: 48%;
    opacity: 0.46;
    background: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0) 68%);
    transform: translate3d(var(--flat-x, 0px), calc(var(--flat-y, 0px) + 5px), -1px) scale(0.94, 0.35);
  }
  /* Every pile layer is a complete card. Sub-pixel registration makes the exposed edges read as
     compressed card stock in top-down view; perspective promotes the same layers into real Z depth. */
  .pile-card-layer {
    overflow: hidden;
    backface-visibility: hidden;
    box-shadow:
      0 0 0 0.45px rgba(246, 235, 206, 0.52),
      0 0.7px 0.7px rgba(22, 17, 14, 0.58);
  }
  .pile-card-layer::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    box-shadow:
      inset 0 0 0 0.7px rgba(246, 232, 197, 0.34),
      inset 0 -1px 0 rgba(18, 14, 12, 0.28);
  }
  .pile-card-layer:not(.pile-card-layer-top) .pile-card-art {
    filter: brightness(0.82) saturate(0.78);
  }
  .pile-card-layer-top {
    box-shadow:
      inset 0 0 0 1px color-mix(in oklab, currentColor 48%, transparent),
      0 3px 3px rgba(0, 0, 0, 0.48);
  }

  .duelist-lp {
    color: var(--color-ink);
    -webkit-text-stroke: 0.8px #05070d;
    paint-order: stroke fill;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.95);
  }

  /* ── Perspective board ── one camera owns the entire gameplay plane. Cards, sockets and the stable
     data-mref motion targets therefore share the same projected coordinates when the view toggles. */
  .mat3d-stage {
    perspective: 1080px;
    perspective-origin: 50% 56%;
  }
  .duel-plane { transition: transform 260ms var(--ease-out-quint, cubic-bezier(0.22, 1, 0.36, 1)); }
  .mat3d {
    transform: translateY(1%) rotateX(28deg) scale(1.06);
    transform-origin: 50% 56%;
    transform-style: preserve-3d;
    will-change: transform;
  }
  .mat3d .field-card-shadow {
    opacity: 0.58;
    transform: translate3d(0, 7px, 0) scale(0.89, 0.3);
  }
  .mat3d .pile-ground-shadow {
    opacity: 0.64;
    transform: translate3d(var(--depth-x, 0px), calc(var(--depth-y, 0px) + 7px), 0) scale(0.94, 0.32);
  }
  .mat3d .pile-motion-anchor,
  .mat3d .pile-card-layer,
  .mat3d .pile-follows-top {
    transform: translate3d(var(--depth-x, 0px), var(--depth-y, 0px), var(--depth-z, 0px));
  }
  .mat3d .pile-card-layer {
    box-shadow:
      0 0 0 0.55px rgba(250, 238, 207, 0.62),
      0 1px 1.2px rgba(18, 13, 10, 0.72);
  }
  .mat3d .chain-levit {
    translate: 0 -16px;
    scale: 1.1;
    filter: none;
  }
  /* Phone camera: with both hands edge-tucked the field is the star — larger on glass, a touch more
     recession (the far rows compress instead of colliding with the opponent's tucked fan). */
  :global(.phone) .mat3d-stage { perspective: 950px; perspective-origin: 50% 54%; }
  :global(.phone) .mat3d {
    transform: translateY(2%) rotateX(24deg) scale(1.12);
    transform-origin: 50% 54%;
  }
  @media (prefers-reduced-motion: reduce) {
    .duel-plane { transition: none; }
  }

  .shake { animation: matshake 0.42s cubic-bezier(0.16, 1, 0.3, 1); }
  /* the individual `translate` property, NOT `transform` — it composes with the 3D board's rotateX
     instead of replacing it (the old transform keyframes snapped the tilted mat flat mid-shake) */
  @keyframes matshake {
    0% { translate: 0 0; }
    16% { translate: -5px 1px; }
    34% { translate: 4px -1px; }
    52% { translate: -3px 1px; }
    70% { translate: 2px 0; }
    85% { translate: -1px 0; }
    100% { translate: 0 0; }
  }

  .fx-beam {
    width: 4px; height: 30vh;
    background: linear-gradient(to top, transparent, currentColor 55%, #fff 100%);
    filter: drop-shadow(0 0 14px currentColor);
    border-radius: 2px; transform-origin: center;
    animation: fxbeam 0.5s cubic-bezier(0.2, 0.85, 0.2, 1) forwards;
  }
  @keyframes fxbeam {
    0% { opacity: 0; transform: translate(-50%, calc(var(--dir) * 30vh)) scaleY(0.4); }
    35% { opacity: 1; }
    100% { opacity: 0; transform: translate(-50%, calc(var(--dir) * -7vh)) scaleY(1.25); }
  }

  .fx-num {
    font-family: var(--font-display); font-weight: 800;
    font-size: clamp(2.4rem, 5.5vw, 4.4rem); line-height: 1;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.85), 0 0 22px currentColor;
    animation: fxnum 1.6s cubic-bezier(0.18, 0.9, 0.3, 1) forwards;
  }
  @keyframes fxnum {
    /* SLAM (animation-plan §3.2): crash in oversized and decelerate hard onto the rest scale within
       the first ~90ms (6% of 1.6s), hold the beat, then the drift/fade. No overshoot — it lands. */
    0% { transform: translate(-50%, 0) scale(1.7); opacity: 0.4; }
    6% { transform: translate(-50%, 0) scale(1); opacity: 1; }
    30% { transform: translate(-50%, 0) scale(1); opacity: 1; }
    100% { transform: translate(-50%, -46px) scale(1); opacity: 0; }
  }

  .fx-toast {
    font-family: var(--font-display); font-weight: 800;
    font-size: clamp(1.6rem, 4vw, 3rem); letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--color-ink);
    text-shadow: 0 0 26px var(--color-cyan), 0 2px 8px rgba(0, 0, 0, 0.7);
    animation: fxtoast 1.5s ease-out forwards;
  }
  @keyframes fxtoast {
    0% { transform: translate(-50%, 0) scale(0.7); opacity: 0; }
    18% { transform: translate(-50%, 0) scale(1.06); opacity: 1; }
    70% { opacity: 1; }
    100% { transform: translate(-50%, 0) scale(1); opacity: 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    .fx-num, .fx-toast, .fx-beam, .shake, .reticle, .chain-hover-card { animation-duration: 0.01ms; }
    .place-target, .target-mark { animation: none; opacity: 1; }
    .think-dot { animation: none; opacity: 0.9; }
  }
</style>
